import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { taskAPI, clientAPI, projectAPI, userAPI, viewsAPI, aiAPI, settingsAPI, shareAPI, attachmentAPI, subtaskAPI, sprintAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Search, Plus, RefreshCw, Filter, Inbox, Loader2, Calendar,
  ChevronDown, ChevronRight, X, Upload, ArrowUp, ArrowDown,
  ListTodo, Columns3, Bookmark, BookmarkPlus, Sparkles,
  MessageSquare, History, Send, Trash2, FolderOpen, GripVertical, Check,
  Share2, Paperclip, Link2, FileText, Image, UserPlus, Settings2, Eye, EyeOff,
  GanttChart, Edit2,
  Download,
  ListChecks,
  ClipboardList,
  Clock,
  AlertTriangle,
  Archive,
  ArchiveRestore,
  MoreVertical,
} from 'lucide-react';
import ResourceTimeline from './ResourceTimeline';
import { MultiSelectFilter } from '../components/MultiSelectFilter';

const DEFAULT_STATUS_CONFIG = {
  backlog: { label: 'Backlog', color: '#6b7280' },
  todo: { label: 'To Do', color: '#f59e0b' },
  in_progress: { label: 'In Progress', color: '#3b82f6' },
  ready_for_testing: { label: 'Ready for Testing', color: '#8b5cf6' },
  in_testing: { label: 'In Testing', color: '#8b5cf6' },
  ready_for_delivery: { label: 'Ready for delivery', color: '#0ea5e9' },
  released: { label: 'Released', color: '#22c55e' },
  done: { label: 'Done', color: '#10b981' },
  cancelled: { label: 'Cancelled', color: '#ef4444' },
};

const PRIORITY_CONFIG = {
  none: { label: 'NONE', icon: '\u2298', color: '#4b5563' },
  low: { label: 'LOW', icon: '\u2193', color: '#6b7280' },
  medium: { label: 'MEDIUM', icon: '\u2261', color: '#f59e0b' },
  high: { label: 'HIGH', icon: '\u2191', color: '#f97316' },
  urgent: { label: 'URGENT', icon: '!', color: '#ef4444' },
};

const PRIORITY_ORDER = { urgent: 0, high: 1, medium: 2, low: 3, none: 4 };

const USE_CASE_OPTIONS = [
  'Increase sales/revenue', 'Customer requirement', 'Internal improvement',
  'Cost reduction', 'Compliance/Regulatory', 'New product/feature',
  'Bug fix', 'Technical debt', 'Security', 'Other',
];

const FUNCTIONAL_AREA_OPTIONS = [
  'Sales', 'Operations', 'Finance', 'Technology', 'HR',
  'Marketing', 'Customer Support', 'Legal', 'Management', 'Other',
];

const DATE_FIELD_OPTIONS = [
  { value: 'created_at', label: 'Created Date' },
  { value: 'start_date', label: 'Start Date' },
  { value: 'due_date', label: 'Due Date' },
  { value: 'release_date', label: 'Release Date' },
  { value: 'targeted_release_date', label: 'Target Release Date' },
];

const FIELD_LABELS = {
  title: 'Title', description: 'Description', status: 'Status', priority: 'Priority',
  start_date: 'Start Date', due_date: 'Due Date', release_date: 'Release Date',
  targeted_release_date: 'Target Release', module: 'Module', cycle: 'Cycle',
  labels: 'Labels', business: 'Business', use_case: 'Use Case',
  functional_area: 'Functional Area', estimated: 'Estimated', assignees: 'Assignees',
};

const getStatusConfig = (s) => DEFAULT_STATUS_CONFIG[s] || { label: s, color: '#6b7280' };
const getPriorityConfig = (p) => PRIORITY_CONFIG[p] || PRIORITY_CONFIG.none;
const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const formatDateInput = (d) => d ? d.substring(0, 10) : '';
const formatFullDateTime = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
const timeAgo = (d) => {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(d);
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0]?.toUpperCase() || '?';
};

/* ===== DEFAULT COLUMN CONFIG ===== */
const ALL_COLUMNS = [
  { id: 'project', label: 'Project', minWidth: 130, sortable: false },
  { id: 'start_date', label: 'Start Date', minWidth: 110, sortable: true },
  { id: 'due_date', label: 'Due Date', minWidth: 110, sortable: true },
  { id: 'release_date', label: 'Release Date', minWidth: 110, sortable: true },
  { id: 'targeted_release_date', label: 'Target Release', minWidth: 110, sortable: true },
  { id: 'status', label: 'State', minWidth: 120, sortable: false },
  { id: 'priority', label: 'Priority', minWidth: 90, sortable: true },
  { id: 'assignees', label: 'Assignees', minWidth: 110, sortable: false },
  { id: 'module', label: 'Module', minWidth: 90, sortable: false },
  { id: 'labels', label: 'Labels', minWidth: 110, sortable: false },
  { id: 'created_at', label: 'Created', minWidth: 110, sortable: true },
  { id: 'updated_at', label: 'Updated', minWidth: 100, sortable: true },
  { id: 'business', label: 'Business', minWidth: 100, sortable: false },
  { id: 'use_case', label: 'Use Case', minWidth: 120, sortable: false },
  { id: 'functional_area', label: 'Functional Area', minWidth: 120, sortable: false },
  { id: 'estimated', label: 'Estimated', minWidth: 80, sortable: false },
  { id: 'sprint_bucket', label: 'Sprint', minWidth: 120, sortable: true },
];

const DEFAULT_VISIBLE = ['project', 'sprint_bucket', 'start_date', 'due_date', 'release_date', 'targeted_release_date', 'status', 'priority', 'assignees', 'module', 'labels', 'created_at', 'updated_at'];

const STORAGE_KEY = 'planboard_columns_v2';
const loadColumnConfig = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Validate - ensure all IDs exist
      const validIds = ALL_COLUMNS.map(c => c.id);
      const order = parsed.order.filter(id => validIds.includes(id));
      // Add any new columns not in saved config
      validIds.forEach(id => { if (!order.includes(id)) order.push(id); });
      return { visible: parsed.visible.filter(id => validIds.includes(id)), order };
    }
  } catch (e) { /* ignore */ }
  return { visible: DEFAULT_VISIBLE, order: ALL_COLUMNS.map(c => c.id) };
};

const saveColumnConfig = (config) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
};

/* ===== INLINE CELL COMPONENTS ===== */
const InlineDateCell = ({ value, taskId, field, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(formatDateInput(value));
  const ref = useRef(null);

  useEffect(() => { if (editing && ref.current) ref.current.focus(); }, [editing]);
  const save = () => { setEditing(false); if (val !== formatDateInput(value)) onSave(taskId, { [field]: val }); };

  if (!editing) return (
    <span className="text-xs cursor-pointer hover:bg-slate-100 px-1 py-0.5 rounded inline-block min-w-[60px]"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }} data-testid={`inline-${field}-${taskId}`}>
      {formatDate(value)}
    </span>
  );
  return (
    <input ref={ref} type="date" value={val} onChange={(e) => setVal(e.target.value)}
      onBlur={save} onKeyDown={(e) => e.key === 'Enter' && save()} onClick={(e) => e.stopPropagation()}
      className="text-xs border border-blue-300 rounded px-1 py-0.5 outline-none w-[120px] bg-white"
      data-testid={`inline-${field}-edit-${taskId}`} />
  );
};

const InlineStatusCell = ({ value, taskId, onSave, statusOptions }) => {
  const [editing, setEditing] = useState(false);
  const opts = statusOptions || DEFAULT_STATUS_CONFIG;
  const sCfg = opts[value] || { label: value, color: '#6b7280' };
  if (!editing) return (
    <span className="text-xs px-2 py-0.5 rounded-full cursor-pointer hover:ring-2 hover:ring-blue-200 transition-all"
      style={{ background: `${sCfg.color}15`, color: sCfg.color }}
      onClick={(e) => { e.stopPropagation(); setEditing(true); }} data-testid={`inline-status-${taskId}`}>
      {sCfg.label}
    </span>
  );
  return (
    <select autoFocus value={value} onClick={(e) => e.stopPropagation()}
      onChange={(e) => { setEditing(false); onSave(taskId, { status: e.target.value }); }}
      onBlur={() => setEditing(false)}
      className="text-xs border border-blue-300 rounded px-1 py-0.5 outline-none bg-white"
      data-testid={`inline-status-edit-${taskId}`}>
      {Object.entries(opts).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
    </select>
  );
};

const InlinePriorityCell = ({ value, taskId, onSave }) => {
  const [editing, setEditing] = useState(false);
  const pCfg = getPriorityConfig(value);
  if (!editing) return (
    <span className="text-xs font-semibold cursor-pointer hover:bg-slate-100 px-1 py-0.5 rounded"
      style={{ color: pCfg.color }}
      onClick={(e) => { e.stopPropagation(); setEditing(true); }} data-testid={`inline-priority-${taskId}`}>
      {pCfg.icon} {pCfg.label}
    </span>
  );
  return (
    <select autoFocus value={value} onClick={(e) => e.stopPropagation()}
      onChange={(e) => { setEditing(false); onSave(taskId, { priority: e.target.value }); }}
      onBlur={() => setEditing(false)}
      className="text-xs border border-blue-300 rounded px-1 py-0.5 outline-none bg-white"
      data-testid={`inline-priority-edit-${taskId}`}>
      {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
    </select>
  );
};

const InlineAssigneeCell = ({ item, users, taskId, onSave }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
  const assignees = item.assignees || [];
  const assigneeIds = assignees.map(a => a.id || a);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target) && triggerRef.current && !triggerRef.current.contains(e.target)) { setOpen(false); setSearch(''); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const openDropdown = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 4, left: Math.max(rect.left, 8) });
    }
    setOpen(!open);
    setSearch('');
  };

  const toggle = (uid) => {
    const newIds = assigneeIds.includes(uid) ? assigneeIds.filter(id => id !== uid) : [...assigneeIds, uid];
    onSave(taskId, { assignees: newIds });
  };

  const filtered = users.filter(u => !search || u.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <div ref={triggerRef} className="flex items-center gap-1 cursor-pointer group" onClick={openDropdown} data-testid={`inline-assignee-${taskId}`}>
        {assignees.length === 0 ? (
          <span className="text-slate-300 text-xs flex items-center gap-0.5 hover:text-blue-500"><UserPlus size={12} /> Add</span>
        ) : (
          <div className="flex -space-x-1.5">
            {assignees.slice(0, 3).map((a, i) => (
              <div key={i} className="w-6 h-6 rounded-full bg-[#002FA7]/10 border-2 border-white flex items-center justify-center" title={a.name || a}>
                <span className="text-[9px] font-bold text-[#002FA7]">{getInitials(a.name)}</span>
              </div>
            ))}
            {assignees.length > 3 && <div className="w-6 h-6 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center"><span className="text-[9px] text-slate-500">+{assignees.length - 3}</span></div>}
          </div>
        )}
        <UserPlus size={11} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      {open && ReactDOM.createPortal(
        <div ref={ref}
          style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, zIndex: 9999 }}
          className="w-56 bg-white border border-slate-200 rounded-lg shadow-xl"
          data-testid={`inline-assignee-dropdown-${taskId}`}>
          <input type="text" placeholder="Search people..." value={search}
            onChange={(e) => { e.stopPropagation(); setSearch(e.target.value); }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            className="w-full px-3 py-2 text-xs border-b border-slate-100 outline-none bg-white rounded-t-lg"
            autoFocus data-testid={`inline-assignee-search-${taskId}`} />
          <div className="overflow-y-auto max-h-48">
            {filtered.length === 0 && <div className="px-3 py-3 text-xs text-slate-400 text-center">No results for "{search}"</div>}
            {filtered.map(u => (
              <div key={u.id} onClick={() => toggle(u.id)}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50 ${assigneeIds.includes(u.id) ? 'bg-blue-50' : ''}`}>
                <span className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-[9px] font-bold text-slate-600">{getInitials(u.name)}</span>
                <span className="flex-1 text-slate-700 truncate">{u.name}</span>
                {assigneeIds.includes(u.id) && <Check size={12} className="text-[#002FA7]" />}
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const APPROVAL_OPTIONS = { pending: { label: 'Pending', cls: 'bg-yellow-50 text-yellow-700 border-yellow-300' }, approved: { label: 'Approved', cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' }, rejected: { label: 'Rejected', cls: 'bg-red-50 text-red-700 border-red-300' } };
const InlineApprovalCell = ({ value, taskId, onSave }) => {
  const [editing, setEditing] = useState(false);
  const ap = APPROVAL_OPTIONS[value] || APPROVAL_OPTIONS.pending;
  if (!editing) return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border cursor-pointer hover:ring-2 hover:ring-blue-200 transition-all ${ap.cls}`}
      onClick={(e) => { e.stopPropagation(); setEditing(true); }} data-testid={`inline-approval-${taskId}`}>{ap.label}</span>
  );
  return (
    <select autoFocus value={value || 'pending'} onClick={(e) => e.stopPropagation()}
      onChange={(e) => { setEditing(false); onSave(taskId, { approval_status: e.target.value }); }}
      onBlur={() => setEditing(false)}
      className="text-xs border border-blue-300 rounded px-1 py-0.5 outline-none bg-white">
      {Object.entries(APPROVAL_OPTIONS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
    </select>
  );
};


const InlineModuleCell = ({ value, taskId, modules, onSave }) => {
  const [editing, setEditing] = useState(false);
  if (!editing) return (
    <span className="text-xs text-slate-500 cursor-pointer hover:bg-slate-100 px-1 py-0.5 rounded inline-block min-w-[40px]"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }} data-testid={`inline-module-${taskId}`}>
      {value || '-'}
    </span>
  );
  return (
    <select autoFocus value={value || ''} onClick={(e) => e.stopPropagation()}
      onChange={(e) => { setEditing(false); onSave(taskId, { module: e.target.value || null }); }}
      onBlur={() => setEditing(false)}
      className="text-xs border border-blue-300 rounded px-1 py-0.5 outline-none bg-white max-w-[120px]"
      data-testid={`inline-module-edit-${taskId}`}>
      <option value="">None</option>
      {modules.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
    </select>
  );
};

/* ===== COLUMN CONFIG PANEL ===== */
const ColumnConfigPanel = ({ columnConfig, setColumnConfig, onClose }) => {
  const { visible, order } = columnConfig;
  const dragItem = useRef(null);
  const dragOver = useRef(null);

  const toggleColumn = (id) => {
    const newVisible = visible.includes(id) ? visible.filter(v => v !== id) : [...visible, id];
    const newConfig = { visible: newVisible, order };
    setColumnConfig(newConfig);
    saveColumnConfig(newConfig);
  };

  const moveColumn = (fromIdx, toIdx) => {
    const newOrder = [...order];
    const [moved] = newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, moved);
    const newConfig = { visible, order: newOrder };
    setColumnConfig(newConfig);
    saveColumnConfig(newConfig);
  };

  const handleDragStart = (idx) => { dragItem.current = idx; };
  const handleDragEnter = (idx) => { dragOver.current = idx; };
  const handleDragEnd = () => {
    if (dragItem.current !== null && dragOver.current !== null && dragItem.current !== dragOver.current) {
      moveColumn(dragItem.current, dragOver.current);
    }
    dragItem.current = null;
    dragOver.current = null;
  };

  const resetDefaults = () => {
    const def = { visible: DEFAULT_VISIBLE, order: ALL_COLUMNS.map(c => c.id) };
    setColumnConfig(def);
    saveColumnConfig(def);
  };

  return (
    <div className="absolute right-0 top-full mt-1 z-50 w-72 bg-white border border-slate-200 rounded-lg shadow-xl" data-testid="column-config-panel">
      <div className="px-3 py-2.5 border-b border-slate-100 flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-700">Configure Columns</span>
        <div className="flex items-center gap-2">
          <button onClick={resetDefaults} className="text-[10px] text-blue-600 hover:underline">Reset</button>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
        </div>
      </div>
      <div className="p-2 max-h-80 overflow-y-auto space-y-0.5">
        <p className="text-[10px] text-slate-400 px-2 py-1 uppercase tracking-wider">Drag to reorder &middot; Click eye to toggle</p>
        {order.map((colId, idx) => {
          const col = ALL_COLUMNS.find(c => c.id === colId);
          if (!col) return null;
          const isVisible = visible.includes(colId);
          return (
            <div key={colId}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragEnter={() => handleDragEnter(idx)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => e.preventDefault()}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-grab active:cursor-grabbing text-xs transition-colors ${isVisible ? 'bg-blue-50/50' : 'bg-slate-50 opacity-60'}`}
              data-testid={`col-config-${colId}`}>
              <GripVertical size={12} className="text-slate-300 flex-shrink-0" />
              <span className="flex-1 text-slate-700 select-none">{col.label}</span>
              <button onClick={() => toggleColumn(colId)} className="p-0.5 rounded hover:bg-slate-200 transition-colors" data-testid={`col-toggle-${colId}`}>
                {isVisible ? <Eye size={13} className="text-blue-600" /> : <EyeOff size={13} className="text-slate-400" />}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ===== HISTORY VALUE FORMATTER ===== */
const formatHistoryValue = (field, val) => {
  if (val === null || val === undefined || val === '') return <span className="text-slate-300 italic">empty</span>;
  if (field === 'status') return <span className="font-medium" style={{ color: getStatusConfig(val).color }}>{getStatusConfig(val).label}</span>;
  if (field === 'priority') { const p = getPriorityConfig(val); return <span className="font-medium" style={{ color: p.color }}>{p.icon} {p.label}</span>; }
  if (field === 'labels' && Array.isArray(val)) return val.length ? val.join(', ') : <span className="text-slate-300 italic">none</span>;
  if (field === 'assignees' && Array.isArray(val)) return val.length ? val.join(', ') : <span className="text-slate-300 italic">none</span>;
  if (['start_date', 'due_date', 'release_date', 'targeted_release_date'].includes(field)) return formatDate(val);
  if (typeof val === 'string' && val.length > 80) return val.substring(0, 80) + '...';
  return String(val);
};

/* ===== MAIN COMPONENT ===== */
const WorkItems = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [projectBusinessMap, setProjectBusinessMap] = useState({});
  const [columnConfig, setColumnConfig] = useState(loadColumnConfig);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const moreMenuRef = useRef(null);

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    const initial = {};
    Object.keys(DEFAULT_STATUS_CONFIG).forEach(s => { if (s !== 'in_progress') initial[s] = true; });
    return initial;
  });
  const [groupLimits, setGroupLimits] = useState({});
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState([]);
  const [filterLabel, setFilterLabel] = useState([]);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterDateField, setFilterDateField] = useState('created_at');
  const [filterUseCase, setFilterUseCase] = useState('all');
  const [filterFunctionalArea, setFilterFunctionalArea] = useState('all');
  const [filterSprint, setFilterSprint] = useState('all');
  const [filterOverdue, setFilterOverdue] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // Saved views
  const [savedViews, setSavedViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);

  // Task detail
  const [detailTab, setDetailTab] = useState('info');
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [aiSummary, setAiSummary] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiSugLoading, setAiSugLoading] = useState(false);
  const [aiEnhancing, setAiEnhancing] = useState(false);
  const [aiEnhanceChanges, setAiEnhanceChanges] = useState([]);
  const [activityEnhancing, setActivityEnhancing] = useState(false);
  const [activityEnhanceChanges, setActivityEnhanceChanges] = useState([]);

  // Attachments
  const [attachments, setAttachments] = useState([]);
  const [subtasks, setSubtasks] = useState([]);
  const [newSubtask, setNewSubtask] = useState(null);
  const [editingSubtask, setEditingSubtask] = useState(null);
  const [subtaskHistory, setSubtaskHistory] = useState({ id: null, entries: [] });
  const [uploadingFile, setUploadingFile] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [newLinkName, setNewLinkName] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [shareUrl, setShareUrl] = useState('');

  const emptyTask = { title: '', description: '', status: 'todo', priority: 'none', assigned_to: '', assignees: [], labels: [], start_date: '', due_date: '', cycle: '', estimated: '', client_id: '', module: '', business: '', use_case: '', functional_area: '', release_date: '', targeted_release_date: '', task_type: 'regular', estimated_hours: '', actual_hours: '' };
  const [newTask, setNewTask] = useState(emptyTask);

  const [masterLabels, setMasterLabels] = useState([]);
  const [masterModules, setMasterModules] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [sprintCompleteOpen, setSprintCompleteOpen] = useState(false);
  const [sprintToComplete, setSprintToComplete] = useState(null);
  const [sprintPendingTasks, setSprintPendingTasks] = useState([]);
  const [sprintCompletedCount, setSprintCompletedCount] = useState(0);
  const [sprintTotalCount, setSprintTotalCount] = useState(0);
  const [sprintMoveTarget, setSprintMoveTarget] = useState('backlog');
  const [sprintCompleting, setSprintCompleting] = useState(false);
  const [statusConfig, setStatusConfig] = useState(DEFAULT_STATUS_CONFIG);
  const [assigneeDropdownOpen, setAssigneeDropdownOpen] = useState(null);
  const [labelDropdownOpen, setLabelDropdownOpen] = useState(null);
  const [assigneeSearch, setAssigneeSearch] = useState('');

  // Activities state
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activityFilter, setActivityFilter] = useState('all'); // all, pending, approved, rejected
  const [activityTypeFilter, setActivityTypeFilter] = useState('all'); // all, meeting, training, support
  const [createActivityOpen, setCreateActivityOpen] = useState(false);
  const emptyActivity = { title: '', description: '', task_type: 'meeting', start_date: '', due_date: '', estimated_hours: '', actual_hours: '', client_id: '', assignees: [] };
  const [newActivity, setNewActivity] = useState(emptyActivity);
  const hasInitializedAssigneeFilter = useRef(false);

  // Read URL params for filters on mount
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    const statusParam = searchParams.get('status');
    const viewParam = searchParams.get('view');
    const typeParam = searchParams.get('type');
    let needsClear = false;

    if (filterParam === 'overdue') {
      setFilterOverdue(true);
      setShowFilters(true);
      const scopeParam = searchParams.get('scope');
      const isDeveloper = user?.role && ['developer', 'support', 'user'].includes(user.role);
      if ((scopeParam === 'my' || isDeveloper) && user?.id) {
        setFilterAssignee([user.id]);
      } else {
        setFilterAssignee([]);
      }
      hasInitializedAssigneeFilter.current = true;
      needsClear = true;
    }

    if (statusParam) {
      const statuses = statusParam.split(',').map(s => s.trim()).filter(Boolean);
      if (statuses.length > 0) {
        setFilterStatus(statuses);
        setShowFilters(true);
        const scopeParam = searchParams.get('scope');
        const isDeveloper = user?.role && ['developer', 'support', 'user'].includes(user.role);
        if ((scopeParam === 'my' || isDeveloper) && user?.id) {
          setFilterAssignee([user.id]);
        } else {
          setFilterAssignee([]);
        }
        hasInitializedAssigneeFilter.current = true;
      }
      needsClear = true;
    }

    if (viewParam === 'activities') {
      setViewMode('activities');
      if (typeParam) {
        setActivityTypeFilter(typeParam);
      }
      if (filterParam === 'overdue') {
        setFilterOverdue(true);
        setShowFilters(true);
      } else if (filterParam === 'open') {
        setFilterStatus(['todo', 'in_progress', 'backlog', 'ready_for_testing']);
        setShowFilters(true);
      }
      hasInitializedAssigneeFilter.current = true;
      needsClear = true;
    }

    if (needsClear) {
      searchParams.delete('filter');
      searchParams.delete('scope');
      searchParams.delete('status');
      searchParams.delete('view');
      searchParams.delete('type');
      setSearchParams(searchParams, { replace: true });
    } else {
      // Handle bare ?scope=my (e.g. Total Tasks in My Tasks mode)
      const scopeParam = searchParams.get('scope');
      if (scopeParam === 'my' && user?.id) {
        setFilterAssignee([user.id]);
        setShowFilters(true);
        hasInitializedAssigneeFilter.current = true;
        searchParams.delete('scope');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, []);

  useEffect(() => { fetchInitialData(); }, []);
  useEffect(() => { setSelectedTasks([]); if (viewMode === 'activities') { fetchActivities(); } else { fetchWorkItems(); } }, [selectedProject, searchQuery, filterDateFrom, filterDateTo, filterDateField, viewMode, activityFilter, activityTypeFilter, filterAssignee, filterStatus, filterLabel, showArchived]);

  // Close more menu on outside click
  useEffect(() => {
    const handler = (e) => { if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) setMoreMenuOpen(false); };
    if (moreMenuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [moreMenuOpen]);

  // Default assignee filter to current user ONLY on initial load
  useEffect(() => {
    if (!hasInitializedAssigneeFilter.current && items.length > 0 && user?.id) {
      const userHasTasks = items.some(item => 
        (item.assignees || []).some(a => (a.id || a) === user.id)
      );
      if (userHasTasks) setFilterAssignee([user.id]);
      hasInitializedAssigneeFilter.current = true;
    }
  }, [items]);

  const fetchInitialData = async () => {
    try {
      const [clientsRes, projectsRes, viewsRes, labelsRes, modulesRes, bizMapRes, statusesRes, sprintsRes] = await Promise.all([
        clientAPI.getClients().catch(() => ({ data: [] })),
        projectAPI.getProjects().catch(() => ({ data: [] })),
        viewsAPI.getViews().catch(() => ({ data: [] })),
        settingsAPI.getLabels().catch(() => ({ data: [] })),
        settingsAPI.getModules().catch(() => ({ data: [] })),
        clientAPI.getProjectBusinessMap().catch(() => ({ data: {} })),
        settingsAPI.getStatuses().catch(() => ({ data: [] })),
        sprintAPI.getSprints().catch(() => ({ data: [] })),
      ]);
      const usersRes = await userAPI.getUsers().catch(() => ({ data: [] }));
      setClients(clientsRes.data || []);
      setProjects(projectsRes.data || []);
      const loadedUsers = usersRes.data || [];
      setUsers(loadedUsers);
      setSavedViews(viewsRes.data || []);
      // Auto-load most recent saved view as default (only on clean load, not from URL params)
      const views = viewsRes.data || [];
      if (views.length > 0 && !searchParams.get('status') && !searchParams.get('filter') && !searchParams.get('view') && !searchParams.get('scope')) {
        const latest = views.reduce((a, b) => (a.created_at || '') > (b.created_at || '') ? a : b);
        if (latest.columns && latest.columns.visible && latest.columns.order) {
          const validIds = ALL_COLUMNS.map(c => c.id);
          const order = latest.columns.order.filter(id => validIds.includes(id));
          validIds.forEach(id => { if (!order.includes(id)) order.push(id); });
          setColumnConfig({ visible: latest.columns.visible.filter(id => validIds.includes(id)), order });
        }
        setActiveViewId(latest.id);
      }
      setMasterLabels(labelsRes.data || []);
      setMasterModules(modulesRes.data || []);
      setProjectBusinessMap(bizMapRes.data || {});
      setSprints(sprintsRes.data || []);
      // Build dynamic statusConfig from master statuses
      const dbStatuses = statusesRes.data || [];
      if (dbStatuses.length > 0) {
        const config = {};
        dbStatuses.forEach(s => {
          const key = s.key || s.value || s.name?.toLowerCase().replace(/\s+/g, '_');
          config[key] = { label: s.name || s.label, color: s.color };
        });
        setStatusConfig(config);
      }
      // If users list is empty (developer 403), load from project members
      if (loadedUsers.length === 0) {
        const projects = projectsRes.data || [];
        const memberMap = {};
        for (const proj of projects) {
          try {
            const membersRes = await projectAPI.getMembers(proj.id);
            (membersRes.data || []).forEach(m => {
              if (m && m.id) memberMap[m.id] = { id: m.id, name: m.name || m.email || m.id, email: m.email, role: m.role };
            });
          } catch (e) { /* skip */ }
        }
        const memberList = Object.values(memberMap);
        if (memberList.length > 0) setUsers(memberList);
      }
    } catch (err) { console.error('Init error:', err); }
  };

  const fetchWorkItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedProject !== 'all') params.client_id = selectedProject;
      if (searchQuery) params.search = searchQuery;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo + 'T23:59:59';
      if (filterDateField) params.date_field = filterDateField;
      if (showArchived) params.show_archived = 'only';
      const response = await taskAPI.getTasks(params);
      setItems(response.data || []);
    } catch (err) { setItems([]); } finally { setLoading(false); }
  }, [selectedProject, searchQuery, filterDateFrom, filterDateTo, filterDateField, showArchived]);

  const fetchActivities = useCallback(async () => {
    setActivitiesLoading(true);
    try {
      const params = {};
      if (selectedProject !== 'all') params.project_id = selectedProject;
      if (searchQuery) params.search = searchQuery;
      if (activityFilter !== 'all') params.approval_status = activityFilter;
      if (activityTypeFilter !== 'all') params.task_type = activityTypeFilter;
      if (filterAssignee.length > 0) params.assignees = filterAssignee.join(',');
      if (filterStatus.length > 0) params.status = filterStatus.join(',');
      if (filterLabel.length > 0) params.labels = filterLabel.join(',');
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (filterOverdue) params.overdue = 'true';
      const response = await taskAPI.getActivities(params);
      setActivities(response.data || []);
    } catch (err) { setActivities([]); } finally { setActivitiesLoading(false); }
  }, [selectedProject, searchQuery, activityFilter, activityTypeFilter, filterAssignee, filterStatus, filterLabel, filterDateFrom, filterDateTo, filterOverdue]);

  const handleCreateActivity = async () => {
    if (!newActivity.title.trim()) return;
    if (!newActivity.client_id) { toast.error('Please select a Project'); return; }
    if (!newActivity.description?.trim()) { toast.error('Please enter a Description'); return; }
    if (!newActivity.start_date) { toast.error('Please select a Start Date'); return; }
    if (!newActivity.due_date) { toast.error('Please select a Due Date'); return; }
    if (!newActivity.estimated_hours) { toast.error('Please enter Estimated Hours'); return; }
    setCreating(true);
    try {
      await taskAPI.createTask({
        title: newActivity.title,
        description: newActivity.description || null,
        task_type: newActivity.task_type,
        start_date: newActivity.start_date || null,
        due_date: newActivity.due_date || null,
        estimated_hours: newActivity.estimated_hours ? parseFloat(newActivity.estimated_hours) : null,
        actual_hours: newActivity.actual_hours ? parseFloat(newActivity.actual_hours) : null,
        client_id: newActivity.client_id || null,
        status: 'todo',
        priority: 'none',
        assignees: newActivity.assignees?.length > 0 ? newActivity.assignees : [user?.id].filter(Boolean),
      });
      setCreateActivityOpen(false);
      setNewActivity({ title: '', description: '', task_type: 'meeting', start_date: '', due_date: '', estimated_hours: '', actual_hours: '', client_id: '', assignees: [] });
      setActivityEnhanceChanges([]);
      fetchActivities();
    } catch (err) { console.error('Create activity error:', err); } finally { setCreating(false); }
  };

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) return;
    const projectId = newTask.client_id || (selectedProject !== 'all' ? selectedProject : '');
    if (!projectId) { toast.error('Please select a Project'); return; }
    if (!newTask.sprint_id) { toast.error('Please select a Sprint'); return; }
    if (!newTask.description?.trim()) { toast.error('Please enter a Description'); return; }
    if (!newTask.functional_area) { toast.error('Please select a Functional Area'); return; }
    if (!newTask.use_case) { toast.error('Please select a Use Case'); return; }
    if (!newTask.labels || newTask.labels.length === 0) { toast.error('Please select at least one Label'); return; }
    if (!newTask.estimated_hours) { toast.error('Please enter Estimated Hours'); return; }
    // Check for scheduling overlaps if dates and assignees are set
    if (newTask.assignees?.length > 0 && newTask.start_date && newTask.due_date) {
      try {
        const overlapRes = await taskAPI.checkOverlaps({
          assignee_ids: newTask.assignees,
          start_date: newTask.start_date,
          due_date: newTask.due_date,
        });
        const overlaps = overlapRes.data?.overlaps || [];
        if (overlaps.length > 0) {
          const msgs = overlaps.map(o =>
            `${o.user_name}: conflicts with ${o.conflicting_tasks.map(t => t.task_number).join(', ')}`
          );
          const proceed = window.confirm(`⚠ Schedule Overlap Detected:\n\n${msgs.join('\n')}\n\nDo you still want to create this task?`);
          if (!proceed) return;
        }
      } catch (e) { console.error('Overlap check failed:', e); }
    }
    setCreating(true);
    try {
      await taskAPI.createTask({
        ...newTask, description: newTask.description || null,
        assigned_to: newTask.assigned_to || null,
        start_date: newTask.start_date || null, due_date: newTask.due_date || null,
        cycle: newTask.cycle || null, estimated: newTask.estimated || null,
        sprint_id: newTask.sprint_id || null,
        estimated_hours: newTask.estimated_hours ? parseFloat(newTask.estimated_hours) : null,
        actual_hours: newTask.actual_hours ? parseFloat(newTask.actual_hours) : null,
        task_type: newTask.task_type || 'regular',
        module: newTask.module || null, business: newTask.business || null,
        use_case: newTask.use_case || null, functional_area: newTask.functional_area || null,
        release_date: newTask.release_date || null, targeted_release_date: newTask.targeted_release_date || null,
        client_id: newTask.client_id || (selectedProject !== 'all' ? selectedProject : null),
      });
      setCreateDialogOpen(false);
      setNewTask({ title: '', description: '', status: 'todo', priority: 'none', assigned_to: '', assignees: [], labels: [], start_date: '', due_date: '', cycle: '', estimated: '', client_id: '', module: '', business: '', use_case: '', functional_area: '', release_date: '', targeted_release_date: '', task_type: 'regular', estimated_hours: '', actual_hours: '' });
      setAiSuggestions(null);
      setAiEnhanceChanges([]);
      fetchWorkItems();
    } catch (err) { console.error('Create error:', err); } finally { setCreating(false); }
  };

  const handleOpenDetail = async (item) => {
    setEditingTask({ ...item, assignees: item.assignees?.map(a => typeof a === 'object' ? a.id : a) || [] });
    setDetailOpen(true);
    setDetailTab('info');
    setComments([]); setHistory([]); setAiSummary(''); setAttachments([]); setShareUrl(''); setSubtasks([]);
    try {
      const [commRes, histRes, attachRes, subRes] = await Promise.all([
        taskAPI.getComments(item.id), taskAPI.getHistory(item.id), attachmentAPI.getAttachments(item.id), subtaskAPI.getSubtasks(item.id)
      ]);
      setComments(commRes.data || []);
      setHistory(histRes.data || []);
      setAttachments(attachRes.data || []);
      setSubtasks(subRes.data || []);
    } catch (e) { console.error('Load detail error:', e); }
  };

  const handleInlineSave = async (taskId, fields) => {
    try {
      if (fields.status === 'released') {
        const item = items.find(i => i.id === taskId);
        if (item && !item.release_date) {
          fields.release_date = new Date().toISOString().substring(0, 10);
        }
      }
      if (fields.due_date) {
        const item = items.find(i => i.id === taskId);
        if (item && !item.targeted_release_date) {
          fields.targeted_release_date = fields.due_date;
        }
      }
      const res = await taskAPI.updateTask(taskId, fields);
      setItems(prev => prev.map(i => i.id === taskId ? { ...i, ...res.data } : i));
    } catch (err) { console.error('Inline save error:', err); }
  };

  const handleActivityInlineSave = async (taskId, fields) => {
    try {
      const res = await taskAPI.updateTask(taskId, fields);
      setActivities(prev => prev.map(a => a.id === taskId ? { ...a, ...res.data } : a));
    } catch (err) { console.error('Activity inline save error:', err); }
  };

  const handleShare = async () => {
    if (!editingTask) return;
    try {
      const res = await shareAPI.generateLink(editingTask.id);
      setShareUrl(res.data.share_url);
      navigator.clipboard.writeText(res.data.share_url);
    } catch (e) { console.error('Share error:', e); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingTask) return;
    setUploadingFile(true);
    try {
      const res = await attachmentAPI.uploadFile(editingTask.id, file);
      setAttachments(prev => [res.data, ...prev]);
    } catch (err) { console.error('Upload error:', err); }
    finally { setUploadingFile(false); e.target.value = ''; }
  };

  const handleAddLink = async () => {
    if (!newLinkUrl || !editingTask) return;
    try {
      const res = await attachmentAPI.addLink(editingTask.id, { name: newLinkName || 'Link', url: newLinkUrl });
      setAttachments(prev => [res.data, ...prev]);
      setLinkDialogOpen(false); setNewLinkName(''); setNewLinkUrl('');
    } catch (e) { console.error('Add link error:', e); }
  };

  const handleDeleteAttachment = async (attId) => {
    if (!editingTask) return;
    try {
      await attachmentAPI.deleteAttachment(editingTask.id, attId);
      setAttachments(prev => prev.filter(a => a.id !== attId));
    } catch (e) { console.error('Delete error:', e); }
  };

  const handleSaveEdit = async () => {
    if (!editingTask) return;
    if (!editingTask.estimated_hours) { toast.error('Please enter Estimated Hours'); return; }
    // Check for scheduling overlaps
    if (editingTask.assignees?.length > 0 && editingTask.start_date && editingTask.due_date) {
      try {
        const overlapRes = await taskAPI.checkOverlaps({
          assignee_ids: editingTask.assignees,
          start_date: editingTask.start_date,
          due_date: editingTask.due_date,
          exclude_task_id: editingTask.id,
        });
        const overlaps = overlapRes.data?.overlaps || [];
        if (overlaps.length > 0) {
          const msgs = overlaps.map(o =>
            `${o.user_name}: conflicts with ${o.conflicting_tasks.map(t => t.task_number).join(', ')}`
          );
          const proceed = window.confirm(`⚠ Schedule Overlap Detected:\n\n${msgs.join('\n')}\n\nDo you still want to save?`);
          if (!proceed) return;
        }
      } catch (e) { console.error('Overlap check failed:', e); }
    }
    setSaving(true);
    try {
      const updatePayload = {
        title: editingTask.title, description: editingTask.description || null,
        status: editingTask.status, priority: editingTask.priority,
        labels: editingTask.labels || [],
        start_date: editingTask.start_date ?? null, due_date: editingTask.due_date ?? null,
        cycle: editingTask.cycle || null, business: editingTask.business || null,
        sprint_id: editingTask.sprint_id || null,
        module: editingTask.module || null, estimated: editingTask.estimated || null,
        estimated_hours: editingTask.estimated_hours ? parseFloat(editingTask.estimated_hours) : null,
        actual_hours: editingTask.actual_hours ? parseFloat(editingTask.actual_hours) : null,
        use_case: editingTask.use_case || null, functional_area: editingTask.functional_area || null,
        release_date: editingTask.release_date ?? null, targeted_release_date: editingTask.targeted_release_date ?? null,
        client_id: editingTask.client_id || editingTask.project?.id || null,
      };
      if (isManager) {
        updatePayload.assignees = editingTask.assignees || [];
      }
      await taskAPI.updateTask(editingTask.id, updatePayload);
      setDetailOpen(false); setEditingTask(null);
      if (viewMode === 'activities') { fetchActivities(); } else { fetchWorkItems(); }
    } catch (err) { console.error('Update error:', err); } finally { setSaving(false); }
  };

  const handleSendComment = async () => {
    if (!newComment.trim() || !editingTask) return;
    setSendingComment(true);
    try {
      const res = await taskAPI.addComment(editingTask.id, newComment.trim());
      setComments(prev => [...prev, res.data]);
      setNewComment('');
    } catch (e) { console.error('Comment error:', e); } finally { setSendingComment(false); }
  };

  const handleAiSummary = async () => {
    if (!editingTask) return;
    setAiLoading(true);
    try { const res = await aiAPI.getTaskSummary(editingTask.id); setAiSummary(res.data.summary); }
    catch (e) { setAiSummary('AI unavailable'); } finally { setAiLoading(false); }
  };

  const handleExportExcel = async () => {
    try {
      const params = {};
      if (selectedProject !== 'all') params.client_id = selectedProject;
      if (searchQuery) params.search = searchQuery;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo + 'T23:59:59';
      if (filterDateField) params.date_field = filterDateField;
      if (filterStatus.length > 0) params.status = filterStatus.join(',');
      if (filterPriority !== 'all') params.priority = filterPriority;
      if (filterAssignee.length > 0) params.assignee_ids = filterAssignee.join(',');
      if (filterLabel.length > 0) params.labels = filterLabel.join(',');
      if (filterUseCase !== 'all') params.use_case = filterUseCase;
      if (filterFunctionalArea !== 'all') params.functional_area = filterFunctionalArea;
      if (filterSprint !== 'all') params.sprint = filterSprint;
      if (filterOverdue) params.overdue = 'true';
      const res = await taskAPI.exportExcel(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `tasks_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error('Export error:', e); }
  };

  const handleAiSuggest = async () => {
    if (!newTask.title.trim()) return;
    setAiSugLoading(true);
    try { const res = await aiAPI.getTaskSuggestions({ title: newTask.title, description: newTask.description }); setAiSuggestions(res.data); }
    catch (e) { console.error(e); } finally { setAiSugLoading(false); }
  };

  const applyAiSuggestions = () => {
    if (!aiSuggestions) return;
    setNewTask(prev => ({ ...prev, priority: aiSuggestions.priority || prev.priority, labels: aiSuggestions.labels?.length ? aiSuggestions.labels : prev.labels }));
  };

  const handleEnhanceTask = async () => {
    if (!newTask.title.trim() && !newTask.description?.trim()) return;
    setAiEnhancing(true);
    setAiEnhanceChanges([]);
    try {
      const res = await aiAPI.enhanceText({ title: newTask.title, description: newTask.description || '' });
      const d = res.data;
      if (d.title || d.description) {
        setNewTask(prev => ({ ...prev, title: d.title || prev.title, description: d.description || prev.description }));
      }
      setAiEnhanceChanges(d.changes || []);
      if (d.changes?.length > 0) toast.success(`AI made ${d.changes.length} improvement${d.changes.length > 1 ? 's' : ''}`);
      else toast.info('Text looks good — no changes needed');
    } catch (e) { toast.error('AI enhance failed'); console.error(e); }
    finally { setAiEnhancing(false); }
  };

  const handleEnhanceActivity = async () => {
    if (!newActivity.title.trim() && !newActivity.description?.trim()) return;
    setActivityEnhancing(true);
    setActivityEnhanceChanges([]);
    try {
      const res = await aiAPI.enhanceText({ title: newActivity.title, description: newActivity.description || '' });
      const d = res.data;
      if (d.title || d.description) {
        setNewActivity(prev => ({ ...prev, title: d.title || prev.title, description: d.description || prev.description }));
      }
      setActivityEnhanceChanges(d.changes || []);
      if (d.changes?.length > 0) toast.success(`AI made ${d.changes.length} improvement${d.changes.length > 1 ? 's' : ''}`);
      else toast.info('Text looks good — no changes needed');
    } catch (e) { toast.error('AI enhance failed'); console.error(e); }
    finally { setActivityEnhancing(false); }
  };

  const handleOpenSprintComplete = async (sprint) => {
    try {
      const res = await sprintAPI.getPendingTasks(sprint.id);
      const d = res.data;
      setSprintToComplete(d.sprint);
      setSprintPendingTasks(d.pending_tasks || []);
      setSprintCompletedCount(d.completed_count || 0);
      setSprintTotalCount(d.total_count || 0);
      setSprintMoveTarget('backlog');
      setSprintCompleteOpen(true);
    } catch (e) { toast.error('Failed to load sprint data'); console.error(e); }
  };

  const handleConfirmSprintComplete = async () => {
    if (!sprintToComplete) return;
    setSprintCompleting(true);
    try {
      await sprintAPI.completeSprint(sprintToComplete.id, {
        move_to: sprintMoveTarget,
        task_ids: sprintPendingTasks.map(t => t.id),
      });
      toast.success(`Sprint "${sprintToComplete.name}" completed!`);
      setSprintCompleteOpen(false);
      setSprintToComplete(null);
      const sprintsRes = await sprintAPI.getSprints();
      setSprints(sprintsRes.data || []);
      fetchWorkItems();
    } catch (e) { toast.error('Sprint completion failed'); console.error(e); }
    finally { setSprintCompleting(false); }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData(); formData.append('file', file);
      if (selectedProject !== 'all') formData.append('client_id', selectedProject);
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const token = localStorage.getItem('access_token');
      const resp = await fetch(`${API}/tasks/upload-excel`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Upload failed');
      alert(`Imported ${data.imported_count} tasks`);
      setUploadDialogOpen(false); fetchWorkItems();
    } catch (err) { alert('Upload failed: ' + err.message); } finally { setUploading(false); }
  };

  const handleSaveView = async () => {
    if (!newViewName.trim()) return;
    try {
      const view = await viewsAPI.createView({
        name: newViewName, view_mode: viewMode,
        filters: { status: filterStatus, priority: filterPriority, assignee: filterAssignee, date_from: filterDateFrom, date_to: filterDateTo, client_id: selectedProject, date_field: filterDateField, use_case: filterUseCase, functional_area: filterFunctionalArea },
        sort_field: sortField, sort_dir: sortDir,
        columns: columnConfig,
      });
      setSavedViews(prev => [...prev, view.data]);
      setSaveViewOpen(false); setNewViewName('');
      setActiveViewId(view.data.id);
      toast.success('View saved');
    } catch (e) { console.error('Save view error:', e); }
  };

  const handleLoadView = (view) => {
    setActiveViewId(view.id);
    setViewMode(view.view_mode || 'list');
    setFilterStatus(Array.isArray(view.filters?.status) ? view.filters.status : view.filters?.status && view.filters.status !== 'all' ? [view.filters.status] : []);
    setFilterPriority(view.filters?.priority || 'all');
    setFilterAssignee(Array.isArray(view.filters?.assignee) ? view.filters.assignee : view.filters?.assignee && view.filters.assignee !== 'all' ? [view.filters.assignee] : []);
    setFilterDateFrom(view.filters?.date_from || '');
    setFilterDateTo(view.filters?.date_to || '');
    setFilterDateField(view.filters?.date_field || 'created_at');
    setFilterUseCase(view.filters?.use_case || 'all');
    setFilterFunctionalArea(view.filters?.functional_area || 'all');
    setSelectedProject(view.filters?.client_id || 'all');
    setSortField(view.sort_field || null);
    setSortDir(view.sort_dir || 'asc');
    setShowFilters(true);
    // Restore column config if saved
    if (view.columns && view.columns.visible && view.columns.order) {
      const validIds = ALL_COLUMNS.map(c => c.id);
      const order = view.columns.order.filter(id => validIds.includes(id));
      validIds.forEach(id => { if (!order.includes(id)) order.push(id); });
      const newConfig = { visible: view.columns.visible.filter(id => validIds.includes(id)), order };
      setColumnConfig(newConfig);
      saveColumnConfig(newConfig);
    }
  };

  const handleDeleteView = async (viewId) => {
    try { await viewsAPI.deleteView(viewId); setSavedViews(prev => prev.filter(v => v.id !== viewId)); if (activeViewId === viewId) setActiveViewId(null); } catch (e) { console.error(e); }
  };

  const toggleGroup = (key) => setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleAssignee = (userId, setter) => {
    setter(prev => {
      const list = prev.assignees || [];
      return { ...prev, assignees: list.includes(userId) ? list.filter(id => id !== userId) : [...list, userId] };
    });
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const clearFilters = () => {
    setFilterStatus([]); setFilterPriority('all'); setFilterAssignee([]); setFilterLabel([]);
    setFilterDateFrom(''); setFilterDateTo(''); setFilterDateField('created_at');
    setFilterUseCase('all'); setFilterFunctionalArea('all'); setFilterSprint('all'); setFilterOverdue(false);
    setSortField(null); setActiveViewId(null);
  };

  const activeFilterCount = [filterOverdue, filterStatus.length > 0, filterPriority !== 'all', filterAssignee.length > 0, filterLabel.length > 0, filterDateFrom !== '', filterDateTo !== '', filterUseCase !== 'all', filterFunctionalArea !== 'all', filterSprint !== 'all'].filter(Boolean).length;

  const handleProjectChange = (projectId, setter) => {
    setter(prev => {
      const update = { ...prev, client_id: projectId };
      if (projectId && projectBusinessMap[projectId]) update.business = projectBusinessMap[projectId].primary || '';
      return update;
    });
  };

  const getProjectName = (item) => {
    if (item.project?.name) return item.project.name;
    if (item.project?.id) { const p = projects.find(pr => pr.id === item.project.id); return p?.name || '-'; }
    if (item.client?.name) return item.client.name;
    if (item.client?.id) { const c = clients.find(cl => cl.id === item.client.id); return c?.name || '-'; }
    return '-';
  };

  const filteredItems = useMemo(() => {
    let result = [...items];
    if (filterOverdue) {
      const _tf = new Date();
      const todayStr = `${_tf.getFullYear()}-${String(_tf.getMonth()+1).padStart(2,'0')}-${String(_tf.getDate()).padStart(2,'0')}`;
      result = result.filter(i => 
        i.due_date && i.due_date.slice(0,10) < todayStr && !['done', 'released', 'cancelled'].includes(i.status)
      );
    }
    if (filterStatus.length > 0) result = result.filter(i => filterStatus.includes(i.status));
    if (filterPriority !== 'all') result = result.filter(i => i.priority === filterPriority);
    if (filterAssignee.length > 0) result = result.filter(i => i.assignees?.some(a => filterAssignee.includes(a.id || a)));
    if (filterLabel.length > 0) result = result.filter(i => (i.labels || []).some(l => filterLabel.includes(l)));
    if (filterUseCase !== 'all') result = result.filter(i => i.use_case === filterUseCase);
    if (filterFunctionalArea !== 'all') result = result.filter(i => i.functional_area === filterFunctionalArea);
    if (filterSprint !== 'all') result = result.filter(i => i.sprint_id === filterSprint || (i.cycle || '') === filterSprint);
    return result;
  }, [items, filterOverdue, filterStatus, filterPriority, filterAssignee, filterLabel, filterUseCase, filterFunctionalArea, filterSprint]);

  const sortedItems = useMemo(() => {
    if (!sortField) return filteredItems;
    return [...filteredItems].sort((a, b) => {
      let va = a[sortField], vb = b[sortField];
      if (sortField === 'priority') { va = PRIORITY_ORDER[va] ?? 4; vb = PRIORITY_ORDER[vb] ?? 4; }
      if (va == null) return 1; if (vb == null) return -1;
      const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredItems, sortField, sortDir]);

  const groupedItems = useMemo(() => {
    const groups = {};
    const statusOrder = ['in_progress', ...Object.keys(statusConfig).filter(s => s !== 'in_progress')];
    statusOrder.forEach(s => { groups[s] = []; });
    sortedItems.forEach(item => { const s = item.status || 'backlog'; if (!groups[s]) groups[s] = []; groups[s].push(item); });
    return statusOrder.map(s => ({ state: s, config: getStatusConfig(s), items: groups[s] })).filter(g => g.items.length > 0);
  }, [sortedItems]);

  const boardColumns = useMemo(() => {
    const cols = {};
    ['backlog', 'todo', 'in_progress', 'ready_for_testing', 'done', 'released', 'cancelled'].forEach(s => { cols[s] = []; });
    filteredItems.forEach(item => { const s = item.status || 'backlog'; if (!cols[s]) cols[s] = []; cols[s].push(item); });
    return Object.entries(cols).map(([status, tasks]) => ({ status, config: getStatusConfig(status), tasks }));
  }, [filteredItems]);

  // Ordered visible columns
  const visibleColumns = useMemo(() => {
    return columnConfig.order.filter(id => columnConfig.visible.includes(id)).map(id => ALL_COLUMNS.find(c => c.id === id)).filter(Boolean);
  }, [columnConfig]);

  const SortableHeader = ({ label, field, minWidth, draggable: isDraggable, onDragStart, onDragOver, onDrop, onDragEnd, style = {} }) => (
    <th style={{ minWidth, cursor: isDraggable ? 'grab' : 'pointer', ...style }} className="select-none hover:bg-slate-100 transition-colors"
      onClick={() => handleSort(field)} draggable={isDraggable}
      onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop} onDragEnd={onDragEnd}>
      <div className="flex items-center gap-1">
        {label}
        {sortField === field && (sortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
      </div>
    </th>
  );

  /* ===== RBAC: check if current user can edit a task ===== */
  const isManager = user?.role && ['admin', 'super_admin', 'pm'].includes(user.role);
  const canEditTask = (item) => {
    if (isManager) return true;
    const assigneeIds = (item.assignees || []).map(a => a.id || a);
    const creatorId = item.created_by?.id;
    return assigneeIds.includes(user?.id) || creatorId === user?.id;
  };

  /* ===== RENDER CELL BY COLUMN ID ===== */
  // Header drag-and-drop state
  const [dragCol, setDragCol] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  const handleHeaderDragStart = (e, colId) => {
    setDragCol(colId);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleHeaderDragOver = (e, colId) => {
    e.preventDefault();
    if (colId !== dragCol) setDragOverCol(colId);
  };
  const handleHeaderDrop = (e, targetColId) => {
    e.preventDefault();
    if (!dragCol || dragCol === targetColId) { setDragCol(null); setDragOverCol(null); return; }
    const newOrder = [...columnConfig.order];
    const fromIdx = newOrder.indexOf(dragCol);
    const toIdx = newOrder.indexOf(targetColId);
    if (fromIdx !== -1 && toIdx !== -1) {
      newOrder.splice(fromIdx, 1);
      newOrder.splice(toIdx, 0, dragCol);
      const newConfig = { ...columnConfig, order: newOrder };
      setColumnConfig(newConfig);
      saveColumnConfig(newConfig);
    }
    setDragCol(null);
    setDragOverCol(null);
  };

  const renderCell = (colId, item) => {
    const _t = new Date(); const _today = `${_t.getFullYear()}-${String(_t.getMonth()+1).padStart(2,'0')}-${String(_t.getDate()).padStart(2,'0')}`;
    const isOverdue = item.due_date && item.due_date.slice(0,10) < _today && !['done','released','cancelled'].includes(item.status);
    const editable = canEditTask(item);
    switch (colId) {
      case 'project': return <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded">{getProjectName(item)}</span>;
      case 'start_date': return editable ? <InlineDateCell value={item.start_date} taskId={item.id} field="start_date" onSave={handleInlineSave} /> : <span className="text-[11px] text-slate-500">{item.start_date ? item.start_date.substring(0,10) : '-'}</span>;
      case 'due_date': return editable ? <span className={isOverdue ? 'text-red-600' : ''}><InlineDateCell value={item.due_date} taskId={item.id} field="due_date" onSave={handleInlineSave} /></span> : <span className={`text-[11px] ${isOverdue ? 'text-red-600' : 'text-slate-500'}`}>{item.due_date ? item.due_date.substring(0,10) : '-'}</span>;
      case 'release_date': return editable ? <InlineDateCell value={item.release_date} taskId={item.id} field="release_date" onSave={handleInlineSave} /> : <span className="text-[11px] text-slate-500">{item.release_date ? item.release_date.substring(0,10) : '-'}</span>;
      case 'targeted_release_date': return editable ? <InlineDateCell value={item.targeted_release_date} taskId={item.id} field="targeted_release_date" onSave={handleInlineSave} /> : <span className="text-[11px] text-slate-500">{item.targeted_release_date ? item.targeted_release_date.substring(0,10) : '-'}</span>;
      case 'status': return editable ? <InlineStatusCell value={item.status} taskId={item.id} onSave={handleInlineSave} statusOptions={statusConfig} /> : <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded bg-slate-100 text-slate-600">{item.status?.replace(/_/g,' ')}</span>;
      case 'priority': return editable ? <InlinePriorityCell value={item.priority} taskId={item.id} onSave={handleInlineSave} /> : <span className="text-[10px] font-semibold uppercase text-slate-500">{item.priority || 'none'}</span>;
      case 'assignees': return isManager ? <InlineAssigneeCell item={item} users={users} taskId={item.id} onSave={handleInlineSave} /> : <div className="flex -space-x-1.5">{(item.assignees||[]).slice(0,3).map((a,i)=><div key={i} className="w-6 h-6 rounded-full bg-[#002FA7]/10 border-2 border-white flex items-center justify-center" title={a.name}><span className="text-[9px] font-bold text-[#002FA7]">{getInitials(a.name)}</span></div>)}{(item.assignees||[]).length > 3 && <div className="w-6 h-6 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center"><span className="text-[9px] text-slate-500">+{item.assignees.length-3}</span></div>}</div>;
      case 'module': return editable ? <InlineModuleCell value={item.module} taskId={item.id} modules={masterModules} onSave={handleInlineSave} /> : <span className="text-xs text-slate-500">{item.module || '-'}</span>;
      case 'labels': return (
        <div className="flex gap-1">{item.labels?.slice(0, 2).map((l, i) => <span key={i} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{l}</span>)}
        {item.labels?.length > 2 && <span className="text-[10px] text-slate-400">+{item.labels.length - 2}</span>}</div>
      );
      case 'created_at': return <span className="text-[10px] text-slate-400">{formatDate(item.created_at)}</span>;
      case 'updated_at': return <span className="text-[10px] text-slate-400">{timeAgo(item.updated_at)}</span>;
      case 'business': return <span className="text-xs text-slate-500">{item.business || '-'}</span>;
      case 'use_case': return <span className="text-xs text-slate-500">{item.use_case || '-'}</span>;
      case 'functional_area': return <span className="text-xs text-slate-500">{item.functional_area || '-'}</span>;
      case 'estimated': return <span className="text-xs text-slate-500">{item.estimated || '-'}</span>;
      case 'sprint_bucket': {
        if (item.sprint_id) {
          const sprint = sprints.find(s => s.id === item.sprint_id);
          return <span className="text-xs text-slate-600 font-medium">{sprint?.name || '-'}</span>;
        }
        return <span className="text-xs text-slate-400">-</span>;
      }
      default: return '-';
    }
  };

  return (
    <div className="-m-5 lg:-m-6 flex flex-col" style={{ height: 'calc(100vh - 0px)' }} data-testid="planboard-page">
      {/* Toolbar */}
      <div className="px-3 sm:px-6 py-2 sm:py-3 border-b border-slate-200 bg-white flex-shrink-0 space-y-2">
        {/* Row 1: Title + View Toggle + Create Button */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <h1 className="font-heading text-base sm:text-xl font-bold text-slate-900 whitespace-nowrap">Planboard</h1>
            <div className="flex items-center bg-slate-100 rounded-md p-0.5" data-testid="view-toggle">
              <button onClick={() => setViewMode('list')}
                className={`flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded text-xs font-medium transition-all ${viewMode === 'list' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="view-list-btn">
                <ListTodo size={14} /> <span className="hidden sm:inline">List</span>
              </button>
              <button onClick={() => setViewMode('board')}
                className={`flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded text-xs font-medium transition-all ${viewMode === 'board' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="view-board-btn">
                <Columns3 size={14} /> <span className="hidden sm:inline">Board</span>
              </button>
              <button onClick={() => setViewMode('timeline')}
                className={`flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded text-xs font-medium transition-all ${viewMode === 'timeline' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="view-timeline-btn">
                <GanttChart size={14} /> <span className="hidden sm:inline">Timeline</span>
              </button>
              <button onClick={() => setViewMode('activities')}
                className={`flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded text-xs font-medium transition-all ${viewMode === 'activities' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="view-activities-btn">
                <Clock size={14} /> <span className="hidden sm:inline">Activities</span>
              </button>
            </div>
          </div>
          {viewMode === 'activities' ? (
            <button onClick={() => { setNewActivity(a => ({ ...a, client_id: selectedProject !== 'all' ? selectedProject : '' })); setCreateActivityOpen(true); }}
              className="h-8 px-3 sm:px-4 text-xs font-semibold rounded-md bg-[#002FA7] text-white hover:bg-[#002585] flex items-center gap-1.5 transition-all whitespace-nowrap flex-shrink-0"
              data-testid="new-activity-btn-toolbar">
              <Plus size={14} /> <span className="hidden sm:inline">Log Activity</span><span className="sm:hidden">Log</span>
            </button>
          ) : (
            <button onClick={() => setCreateDialogOpen(true)}
              className="h-8 px-3 sm:px-4 text-xs font-semibold rounded-md bg-[#002FA7] text-white hover:bg-[#002585] flex items-center gap-1.5 transition-all whitespace-nowrap flex-shrink-0"
              data-testid="new-work-item-btn">
              <Plus size={14} /> <span className="hidden sm:inline">Work Item</span><span className="sm:hidden">New</span>
            </button>
          )}
        </div>
        {/* Row 2: Search + Filters + Actions */}
        <div className="flex items-center gap-2">
          <div className="relative flex-shrink-0">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 pr-2 h-8 text-sm border border-slate-200 rounded-md outline-none focus:border-[#002FA7] w-28 sm:w-52" data-testid="search-input" />
          </div>
          <select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)}
            className="h-8 px-2 text-xs sm:text-sm border border-slate-200 rounded-md outline-none bg-white text-slate-700 cursor-pointer focus:border-[#002FA7] flex-shrink-0 max-w-[120px] sm:max-w-none" data-testid="project-filter">
            <option value="all">All Projects</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}{p.client?.name ? ` (${p.client.name})` : ''}</option>)}
          </select>
          <button onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 h-8 px-2.5 sm:px-3 text-xs font-medium rounded-md border transition-colors flex-shrink-0 ${showFilters || activeFilterCount > 0 ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
            data-testid="filter-toggle-btn">
            <Filter size={13} /> <span className="hidden sm:inline">Filters</span> {activeFilterCount > 0 && <span className="bg-white/20 px-1.5 rounded-full text-[10px]">{activeFilterCount}</span>}
          </button>
          {/* Desktop-only actions (hidden on mobile, shown in More menu) */}
          <div className="hidden sm:flex items-center gap-2">
            {viewMode === 'list' && (
              <div className="relative flex-shrink-0">
                <button onClick={() => setColumnConfigOpen(!columnConfigOpen)}
                  className="h-8 px-2.5 text-xs font-medium rounded-md bg-white border border-slate-200 text-slate-600 hover:border-slate-300 flex items-center gap-1.5"
                  data-testid="column-config-btn" title="Configure columns">
                  <Settings2 size={13} /> <span className="hidden sm:inline">Columns</span>
                </button>
                {columnConfigOpen && <ColumnConfigPanel columnConfig={columnConfig} setColumnConfig={setColumnConfig} onClose={() => setColumnConfigOpen(false)} />}
              </div>
            )}
            <button onClick={() => setUploadDialogOpen(true)} className={`h-8 px-2.5 text-xs font-medium rounded-md bg-white border border-slate-200 text-slate-600 hover:border-slate-300 flex items-center gap-1.5 flex-shrink-0 ${viewMode === 'activities' ? 'hidden' : ''}`} data-testid="upload-btn">
              <Upload size={13} /> Import
            </button>
            <button onClick={handleExportExcel} className={`h-8 px-2.5 text-xs font-medium rounded-md bg-white border border-slate-200 text-slate-600 hover:border-slate-300 flex items-center gap-1.5 flex-shrink-0 ${viewMode === 'activities' ? 'hidden' : ''}`} data-testid="export-btn">
              <Download size={13} /> Export
            </button>
            {isManager && viewMode !== 'activities' && (
              <button onClick={() => { setShowArchived(v => !v); setSelectedTasks([]); }}
                className={`h-8 px-2.5 text-xs font-medium rounded-md border flex items-center gap-1.5 transition-all flex-shrink-0 ${showArchived ? 'bg-amber-50 border-amber-300 text-amber-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                data-testid="toggle-archived-btn">
                <Archive size={13} /> {showArchived ? 'Show Active' : 'View Archived'}
              </button>
            )}
            {isManager && viewMode !== 'activities' && sprints.filter(s => s.status === 'active').length > 0 && (
              <div className="relative group flex-shrink-0">
                <button className="h-8 px-2.5 text-xs font-medium rounded-md border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100 flex items-center gap-1.5" data-testid="complete-sprint-dropdown">
                  <Check size={13} /> Complete Sprint
                </button>
                <div className="absolute top-full right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 min-w-[220px] hidden group-hover:block">
                  {sprints.filter(s => s.status === 'active').map(s => (
                    <button key={s.id} onClick={() => handleOpenSprintComplete(s)}
                      className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 first:rounded-t-lg last:rounded-b-lg flex items-center gap-2"
                      data-testid={`complete-sprint-${s.id}`}>
                      <span className="w-2 h-2 rounded-full bg-orange-400" />
                      {s.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center gap-1 flex-shrink-0" data-testid="saved-views-bar">
              {savedViews.map(v => (
                <button key={v.id} onClick={() => handleLoadView(v)}
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all border whitespace-nowrap ${activeViewId === v.id ? 'bg-[#002FA7]/10 border-[#002FA7]/30 text-[#002FA7]' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}
                  data-testid={`view-${v.id}`}>
                  <Bookmark size={10} /> {v.name}
                  <span onClick={(e) => { e.stopPropagation(); handleDeleteView(v.id); }} className="ml-1 hover:text-red-500"><X size={10} /></span>
                </button>
              ))}
              <button onClick={() => setSaveViewOpen(true)} className="flex items-center gap-1 px-2 py-1 rounded-full text-xs text-slate-400 hover:text-[#002FA7] hover:bg-[#002FA7]/5 transition-all flex-shrink-0" data-testid="save-view-btn" title="Save current view">
                <BookmarkPlus size={12} />
              </button>
            </div>
          </div>
          {/* Mobile "More" menu */}
          <div className="sm:hidden relative flex-shrink-0" ref={moreMenuRef}>
            <button onClick={() => setMoreMenuOpen(!moreMenuOpen)}
              className={`h-8 w-8 flex items-center justify-center rounded-md border transition-colors ${moreMenuOpen ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-600 border-slate-200'}`}
              data-testid="more-actions-btn">
              <MoreVertical size={16} />
            </button>
            {moreMenuOpen && (
              <div className="absolute top-full right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl z-50 min-w-[200px] py-1" data-testid="more-actions-menu">
                {viewMode === 'list' && (
                  <button onClick={() => { setColumnConfigOpen(!columnConfigOpen); setMoreMenuOpen(false); }}
                    className="w-full text-left px-3.5 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5">
                    <Settings2 size={14} className="text-slate-400" /> Columns
                  </button>
                )}
                {viewMode !== 'activities' && (
                  <>
                    <button onClick={() => { setUploadDialogOpen(true); setMoreMenuOpen(false); }}
                      className="w-full text-left px-3.5 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5">
                      <Upload size={14} className="text-slate-400" /> Import
                    </button>
                    <button onClick={() => { handleExportExcel(); setMoreMenuOpen(false); }}
                      className="w-full text-left px-3.5 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5">
                      <Download size={14} className="text-slate-400" /> Export
                    </button>
                  </>
                )}
                {isManager && viewMode !== 'activities' && (
                  <button onClick={() => { setShowArchived(v => !v); setSelectedTasks([]); setMoreMenuOpen(false); }}
                    className="w-full text-left px-3.5 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5">
                    <Archive size={14} className="text-slate-400" /> {showArchived ? 'Show Active' : 'View Archived'}
                  </button>
                )}
                {isManager && viewMode !== 'activities' && sprints.filter(s => s.status === 'active').length > 0 && (
                  <>
                    <div className="border-t border-slate-100 my-1" />
                    <div className="px-3.5 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Complete Sprint</div>
                    {sprints.filter(s => s.status === 'active').map(s => (
                      <button key={s.id} onClick={() => { handleOpenSprintComplete(s); setMoreMenuOpen(false); }}
                        className="w-full text-left px-3.5 py-2.5 text-sm text-orange-700 hover:bg-orange-50 flex items-center gap-2.5"
                        data-testid={`complete-sprint-mobile-${s.id}`}>
                        <span className="w-2 h-2 rounded-full bg-orange-400" />
                        {s.name}
                      </button>
                    ))}
                  </>
                )}
                {savedViews.length > 0 && (
                  <>
                    <div className="border-t border-slate-100 my-1" />
                    <div className="px-3.5 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Saved Views</div>
                    {savedViews.map(v => (
                      <button key={v.id} onClick={() => { handleLoadView(v); setMoreMenuOpen(false); }}
                        className={`w-full text-left px-3.5 py-2.5 text-sm hover:bg-slate-50 flex items-center gap-2.5 ${activeViewId === v.id ? 'text-[#002FA7] font-medium' : 'text-slate-700'}`}>
                        <Bookmark size={14} className={activeViewId === v.id ? 'text-[#002FA7]' : 'text-slate-400'} /> {v.name}
                      </button>
                    ))}
                  </>
                )}
                <div className="border-t border-slate-100 my-1" />
                <button onClick={() => { setSaveViewOpen(true); setMoreMenuOpen(false); }}
                  className="w-full text-left px-3.5 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5">
                  <BookmarkPlus size={14} className="text-slate-400" /> Save Current View
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="px-3 sm:px-6 py-2.5 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center gap-2 sm:gap-2.5" data-testid="filter-panel">
          <span className="font-mono text-[10px] text-slate-400 uppercase tracking-wider">Filters</span>
          {filterOverdue && (
            <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-200" data-testid="overdue-filter-badge">
              <AlertTriangle size={12} /> Overdue
              <button onClick={() => setFilterOverdue(false)} className="ml-0.5 hover:text-red-900"><X size={12} /></button>
            </span>
          )}
          <MultiSelectFilter
            options={Object.entries(statusConfig).map(([k, v]) => ({ value: k, label: v.label, dot: v.color }))}
            selected={filterStatus}
            onChange={setFilterStatus}
            placeholder="All Statuses"
            testId="filter-status-select"
          />
          <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700 outline-none focus:border-[#002FA7]" data-testid="filter-priority-select">
            <option value="all">All Priorities</option>
            {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <MultiSelectFilter
            options={users.map(u => ({ value: u.id, label: u.name }))}
            selected={filterAssignee}
            onChange={setFilterAssignee}
            placeholder="All Assignees"
            testId="filter-assignee-select"
          />
          <MultiSelectFilter
            options={masterLabels.map(l => ({ value: l.name, label: l.name, dot: l.color }))}
            selected={filterLabel}
            onChange={setFilterLabel}
            placeholder="All Labels"
            testId="filter-label-select"
          />
          <select value={filterSprint} onChange={(e) => setFilterSprint(e.target.value)} className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700 outline-none focus:border-[#002FA7]" data-testid="filter-sprint-select">
            <option value="all">All Sprints</option>
            {sprints.filter(s => s.status !== 'completed').map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={filterUseCase} onChange={(e) => setFilterUseCase(e.target.value)} className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700 outline-none focus:border-[#002FA7]" data-testid="filter-usecase-select">
            <option value="all">All Use Cases</option>
            {USE_CASE_OPTIONS.map(uc => <option key={uc} value={uc}>{uc}</option>)}
          </select>
          <select value={filterFunctionalArea} onChange={(e) => setFilterFunctionalArea(e.target.value)} className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700 outline-none focus:border-[#002FA7]" data-testid="filter-functional-area-select">
            <option value="all">All Functional Areas</option>
            {FUNCTIONAL_AREA_OPTIONS.map(fa => <option key={fa} value={fa}>{fa}</option>)}
          </select>
          <div className="flex items-center gap-1.5 border-l border-slate-300 pl-2.5 ml-1">
            <select value={filterDateField} onChange={(e) => setFilterDateField(e.target.value)} className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700 outline-none focus:border-[#002FA7]" data-testid="filter-date-field-select">
              {DATE_FIELD_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
            <span className="text-[10px] text-slate-400">From</span>
            <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white outline-none focus:border-[#002FA7]" data-testid="filter-date-from" />
            <span className="text-[10px] text-slate-400">To</span>
            <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white outline-none focus:border-[#002FA7]" data-testid="filter-date-to" />
          </div>
          {activeFilterCount > 0 && <button onClick={clearFilters} className="text-xs text-red-500 hover:underline flex items-center gap-1" data-testid="clear-filters-btn"><X size={12} /> Clear</button>}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto bg-white" data-testid="planboard-content">
        {projects.length === 0 && ['developer', 'support', 'user'].includes(user?.role) ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3" data-testid="no-projects-banner">
            <FolderOpen size={40} className="text-amber-300" />
            <p className="text-slate-700 text-sm font-semibold">No Projects Assigned</p>
            <p className="text-slate-400 text-xs max-w-sm text-center">You haven't been assigned to any projects yet. Please contact your Project Manager to get added to a project.</p>
          </div>
        ) : viewMode === 'activities' ? (
          /* ===== ACTIVITIES VIEW ===== */
          <div className="p-3 sm:p-6" data-testid="activities-view">
            <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2 overflow-x-auto scrollbar-hide">
              <div className="flex items-center gap-2">
                <select value={activityTypeFilter} onChange={(e) => setActivityTypeFilter(e.target.value)}
                  className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700 outline-none focus:border-[#002FA7]" data-testid="activity-type-filter">
                  <option value="all">All Types</option>
                  <option value="meeting">Meeting</option>
                  <option value="training">Training</option>
                  <option value="support">Support</option>
                </select>
                <select value={activityFilter} onChange={(e) => setActivityFilter(e.target.value)}
                  className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700 outline-none focus:border-[#002FA7]" data-testid="activity-status-filter">
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending Approval</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <button onClick={() => { setNewActivity(a => ({ ...a, client_id: selectedProject !== 'all' ? selectedProject : '' })); setCreateActivityOpen(true); }}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-[#002FA7] text-white hover:bg-[#0025a0] transition-colors" data-testid="new-activity-btn">
                <Plus size={14} /> Log Activity
              </button>
            </div>
            {activitiesLoading ? (
              <div className="flex items-center justify-center h-48"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
            ) : activities.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 gap-3">
                <Clock size={36} className="text-slate-300" />
                <p className="text-slate-400 text-sm">No activities logged yet</p>
                <button className="px-4 py-2 text-sm rounded-md bg-[#002FA7] text-white" onClick={() => { setNewActivity(a => ({ ...a, client_id: selectedProject !== 'all' ? selectedProject : '' })); setCreateActivityOpen(true); }}><Plus size={14} className="inline mr-1" />Log Activity</button>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="overflow-x-auto rounded-lg border border-slate-200 hidden md:block">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left px-4 py-2.5 font-semibold text-slate-600 text-xs">ID</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-slate-600 text-xs">Activity</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-slate-600 text-xs">Type</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-slate-600 text-xs">Project</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-slate-600 text-xs">Assignees</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-slate-600 text-xs">Start Date</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-slate-600 text-xs">Due Date</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-slate-600 text-xs">Status</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-slate-600 text-xs">Approval</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-slate-600 text-xs">Labels</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-slate-600 text-xs">Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activities.map((act) => {
                        const typeConfig = { meeting: { label: 'Meeting', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' }, training: { label: 'Training', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' }, support: { label: 'Support', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' } };
                        const tc = typeConfig[act.task_type] || typeConfig.support;
                        const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
                        const assigneeList = act.assignees || [];
                        const _t2 = new Date(); const _today2 = `${_t2.getFullYear()}-${String(_t2.getMonth()+1).padStart(2,'0')}-${String(_t2.getDate()).padStart(2,'0')}`;
                        const isOverdue = act.due_date && act.due_date.slice(0,10) < _today2 && !['done', 'released', 'cancelled'].includes(act.status);
                        return (
                          <tr key={act.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`activity-row-${act.id}`}>
                            <td className="px-4 py-2.5 font-mono text-[11px] text-slate-400 whitespace-nowrap cursor-pointer" onClick={() => handleOpenDetail(act)}>{act.task_number}</td>
                            <td className="px-4 py-2.5 font-medium text-slate-800 max-w-[250px] truncate cursor-pointer" onClick={() => handleOpenDetail(act)}>{act.title}</td>
                            <td className="px-4 py-2.5"><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border ${tc.bg} ${tc.text} ${tc.border}`}>{tc.label}</span></td>
                            <td className="px-4 py-2.5 text-slate-600 text-xs whitespace-nowrap">{act.project?.name || act.client?.name || '-'}</td>
                            <td className="px-4 py-2.5">{isManager ? <InlineAssigneeCell item={act} users={users} taskId={act.id} onSave={handleActivityInlineSave} /> : <div className="flex items-center gap-1">{assigneeList.slice(0, 3).map((a, i) => (<span key={i} className="w-6 h-6 rounded-full bg-slate-200 text-[9px] font-bold flex items-center justify-center text-slate-600 border border-white" title={a.name || a}>{(a.name || a)?.[0]?.toUpperCase()}</span>))}{assigneeList.length > 3 && <span className="text-[10px] text-slate-400">+{assigneeList.length - 3}</span>}{assigneeList.length === 0 && <span className="text-xs text-slate-300">-</span>}</div>}</td>
                            <td className="px-4 py-2.5">{isManager ? <InlineDateCell value={act.start_date} taskId={act.id} field="start_date" onSave={handleActivityInlineSave} /> : <span className="text-slate-500 text-xs whitespace-nowrap">{fmtDate(act.start_date)}</span>}</td>
                            <td className="px-4 py-2.5">{isManager ? <span className={isOverdue ? 'text-red-600' : ''}><InlineDateCell value={act.due_date} taskId={act.id} field="due_date" onSave={handleActivityInlineSave} /></span> : <span className={`text-xs whitespace-nowrap ${isOverdue ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>{fmtDate(act.due_date)}{isOverdue && <span className="ml-1 text-[9px] bg-red-100 text-red-600 px-1 py-0.5 rounded font-semibold">OVERDUE</span>}</span>}</td>
                            <td className="px-4 py-2.5"><InlineStatusCell value={act.status} taskId={act.id} onSave={handleActivityInlineSave} statusOptions={statusConfig} /></td>
                            <td className="px-4 py-2.5">{isManager ? <InlineApprovalCell value={act.approval_status} taskId={act.id} onSave={handleActivityInlineSave} /> : (() => { const ap = APPROVAL_OPTIONS[act.approval_status] || APPROVAL_OPTIONS.pending; return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border ${ap.cls}`}>{ap.label}</span>; })()}</td>
                            <td className="px-4 py-2.5">
                              <div className="flex flex-wrap gap-1">
                                {(act.labels || []).slice(0, 2).map((l, i) => (
                                  <span key={i} className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-slate-100 text-slate-600">{l}</span>
                                ))}
                                {(act.labels || []).length > 2 && <span className="text-[9px] text-slate-400">+{(act.labels || []).length - 2}</span>}
                                {(act.labels || []).length === 0 && <span className="text-xs text-slate-300">-</span>}
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-slate-400 text-[11px] whitespace-nowrap">{act.updated_at ? timeAgo(act.updated_at) : '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card Layout */}
                <div className="md:hidden space-y-2" data-testid="activities-mobile-list">
                  {activities.map((act) => {
                    const typeConfig = { meeting: { label: 'Meeting', bg: 'bg-blue-50', text: 'text-blue-700' }, training: { label: 'Training', bg: 'bg-purple-50', text: 'text-purple-700' }, support: { label: 'Support', bg: 'bg-amber-50', text: 'text-amber-700' } };
                    const tc = typeConfig[act.task_type] || typeConfig.support;
                    const _t2m = new Date(); const _today2m = `${_t2m.getFullYear()}-${String(_t2m.getMonth()+1).padStart(2,'0')}-${String(_t2m.getDate()).padStart(2,'0')}`;
                    const isOverdue = act.due_date && act.due_date.slice(0,10) < _today2m && !['done', 'released', 'cancelled'].includes(act.status);
                    const sCfg = statusConfig[act.status] || { label: act.status, color: '#94a3b8' };
                    const ap = APPROVAL_OPTIONS[act.approval_status] || APPROVAL_OPTIONS.pending;
                    const assigneeList = act.assignees || [];
                    return (
                      <div key={act.id} onClick={() => handleOpenDetail(act)}
                        className={`p-3 rounded-lg border cursor-pointer active:bg-slate-50 transition-colors ${isOverdue ? 'border-red-200 bg-red-50/30' : 'border-slate-200 bg-white'}`}
                        data-testid={`activity-card-${act.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className="text-[10px] font-mono text-slate-400">{act.task_number}</span>
                              <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${tc.bg} ${tc.text}`}>{tc.label}</span>
                              {isOverdue && <span className="text-[9px] font-bold text-red-500 bg-red-100 px-1 rounded">OVERDUE</span>}
                            </div>
                            <p className="text-sm font-medium text-slate-800 leading-snug line-clamp-2">{act.title}</p>
                          </div>
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-semibold border flex-shrink-0 ${ap.cls}`}>{ap.label}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ color: sCfg.color, background: `${sCfg.color}15` }}>{sCfg.label}</span>
                          <span className="text-[10px] font-medium text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded truncate max-w-[120px]">{act.project?.name || act.client?.name || '-'}</span>
                          {act.due_date && (
                            <span className={`text-[10px] flex items-center gap-0.5 ${isOverdue ? 'text-red-500 font-semibold' : 'text-slate-400'}`}>
                              <Calendar size={9} /> {new Date(act.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                            </span>
                          )}
                          <div className="flex -space-x-1.5 ml-auto">
                            {assigneeList.slice(0, 3).map((a, i) => (
                              <div key={i} className="w-5 h-5 rounded-full bg-[#002FA7]/10 border border-white flex items-center justify-center" title={a.name || a}>
                                <span className="text-[8px] font-bold text-[#002FA7]">{(a.name || a)?.[0]?.toUpperCase()}</span>
                              </div>
                            ))}
                            {assigneeList.length > 3 && <span className="text-[9px] text-slate-400 ml-1">+{assigneeList.length - 3}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        ) : viewMode === 'timeline' ? (
          /* ===== TIMELINE VIEW ===== */
          <ResourceTimeline onTaskClick={handleOpenDetail} users={users} projects={projects} selectedProject={selectedProject} filterStatus={filterStatus} filterAssignee={filterAssignee} />
        ) : loading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <Inbox size={40} className="text-slate-300" />
            <p className="text-slate-400 text-sm">{activeFilterCount > 0 ? 'No items match filters' : 'No work items found'}</p>
            <button className="px-4 py-2 text-sm rounded-md bg-[#002FA7] text-white" onClick={() => setCreateDialogOpen(true)}><Plus size={14} className="inline mr-1" />Create Work Item</button>
          </div>
        ) : viewMode === 'list' ? (
          /* ===== LIST VIEW ===== */
          <>
            {/* Desktop Table */}
            <div className="overflow-x-auto hidden md:block">
              <table className="planboard-table" style={{ minWidth: `${300 + visibleColumns.length * 110}px` }} data-testid="planboard-grid">
                <thead>
                  <tr>
                    {isManager && (
                      <th style={{ width: 36, minWidth: 36 }} className="!px-2">
                        <div className="flex items-center justify-center">
                          <label className="relative flex items-center justify-center w-[18px] h-[18px] cursor-pointer">
                            <input type="checkbox" className="peer sr-only" data-testid="select-all-tasks"
                              checked={items.length > 0 && selectedTasks.length === items.length}
                              onChange={(e) => setSelectedTasks(e.target.checked ? items.map(i => i.id) : [])} />
                            <div className="w-[18px] h-[18px] rounded border-2 border-slate-300 peer-checked:border-[#002FA7] peer-checked:bg-[#002FA7] transition-all flex items-center justify-center">
                              <Check size={12} className="text-white opacity-0 peer-checked:opacity-100" />
                            </div>
                            <Check size={12} className="absolute text-white opacity-0 peer-checked:opacity-100 pointer-events-none" />
                          </label>
                        </div>
                      </th>
                    )}
                    <SortableHeader label="Work Items" field="title" minWidth={280} />
                    {visibleColumns.map(col => (
                      col.sortable ? <SortableHeader key={col.id} label={col.label} field={col.id} minWidth={col.minWidth}
                        draggable onDragStart={(e) => handleHeaderDragStart(e, col.id)}
                        onDragOver={(e) => handleHeaderDragOver(e, col.id)}
                        onDrop={(e) => handleHeaderDrop(e, col.id)}
                        onDragEnd={() => { setDragCol(null); setDragOverCol(null); }}
                        style={{ opacity: dragCol === col.id ? 0.4 : 1, borderLeft: dragOverCol === col.id ? '2px solid #002FA7' : undefined }} />
                      : <th key={col.id} style={{ minWidth: col.minWidth, cursor: 'grab', opacity: dragCol === col.id ? 0.4 : 1, borderLeft: dragOverCol === col.id ? '2px solid #002FA7' : undefined }}
                          draggable onDragStart={(e) => handleHeaderDragStart(e, col.id)}
                          onDragOver={(e) => handleHeaderDragOver(e, col.id)}
                          onDrop={(e) => handleHeaderDrop(e, col.id)}
                          onDragEnd={() => { setDragCol(null); setDragOverCol(null); }}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groupedItems.map((group) => (
                    <React.Fragment key={group.state}>
                      <tr className="cursor-pointer select-none group-header-row" onClick={() => toggleGroup(group.state)} data-testid={`group-header-${group.state}`}>
                        {isManager && <td className="!py-2 !px-2 !border-b !border-slate-200 !bg-slate-100" style={{ background: '#f1f5f9' }}></td>}
                        <td className="!py-2 !px-4 !border-b !border-slate-200 !bg-slate-100" style={{ position: 'sticky', left: isManager ? 36 : 0, zIndex: 15, background: '#f1f5f9' }}>
                          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 whitespace-nowrap">
                            {collapsedGroups[group.state] ? <ChevronRight size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                            <span className="w-2 h-2 rounded-full" style={{ background: group.config.color }} />
                            {group.config.label}
                            <span className="text-xs font-normal text-slate-400">({group.items.length})</span>
                          </div>
                        </td>
                        <td className="!border-b !border-slate-200 !bg-slate-100" colSpan={visibleColumns.length} style={{ background: '#f1f5f9' }}></td>
                      </tr>
                      {!collapsedGroups[group.state] && group.items.slice(0, groupLimits[group.state] || 50).map((item) => {
                        const sCfg = getStatusConfig(item.status);
                        const _t3 = new Date(); const _today3 = `${_t3.getFullYear()}-${String(_t3.getMonth()+1).padStart(2,'0')}-${String(_t3.getDate()).padStart(2,'0')}`;
                        const isOverdue = item.due_date && item.due_date.slice(0,10) < _today3 && !['done','released','cancelled'].includes(item.status);
                        return (
                          <tr key={item.id} className={`hover:bg-blue-50/50 ${isOverdue ? 'bg-red-50/30' : ''} ${selectedTasks.includes(item.id) ? 'bg-blue-50' : ''}`} data-testid={`work-item-row-${item.id}`}>
                            {isManager && (
                              <td style={{ width: 36, background: selectedTasks.includes(item.id) ? '#eff6ff' : isOverdue ? '#fef2f2' : '#fff' }} className="!px-2">
                                <div className="flex items-center justify-center">
                                  <label className="relative flex items-center justify-center w-[18px] h-[18px] cursor-pointer">
                                    <input type="checkbox" className="peer sr-only" data-testid={`select-task-${item.id}`}
                                      checked={selectedTasks.includes(item.id)}
                                      onChange={(e) => { e.stopPropagation(); setSelectedTasks(prev => e.target.checked ? [...prev, item.id] : prev.filter(id => id !== item.id)); }} />
                                    <div className="w-[18px] h-[18px] rounded border-2 border-slate-300 peer-checked:border-[#002FA7] peer-checked:bg-[#002FA7] transition-all" />
                                    <Check size={12} className="absolute text-white opacity-0 peer-checked:opacity-100 pointer-events-none" />
                                  </label>
                                </div>
                              </td>
                            )}
                            <td style={{ position: 'sticky', left: isManager ? 36 : 0, zIndex: 10, background: selectedTasks.includes(item.id) ? '#eff6ff' : isOverdue ? '#fef2f2' : '#fff', maxWidth: 280, cursor: 'pointer' }}
                              onClick={() => handleOpenDetail(item)}>
                              <div className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: sCfg.color }} />
                                <div className="min-w-0 flex-1" title={item.title}>
                                  <span className="text-slate-400 font-mono text-[10px]">{item.task_number}</span>
                                  <span className="text-sm text-slate-700 block truncate leading-tight hover:text-[#002FA7] hover:underline">{item.title}</span>
                                </div>
                                {isOverdue && <span className="text-[10px] font-bold text-red-500 bg-red-100 px-1.5 rounded flex-shrink-0">OVERDUE</span>}
                                {item.archived && <span className="text-[10px] font-bold text-amber-600 bg-amber-100 px-1.5 rounded flex-shrink-0">ARCHIVED</span>}
                              </div>
                            </td>
                            {visibleColumns.map(col => <td key={col.id}>{renderCell(col.id, item)}</td>)}
                          </tr>
                        );
                      })}
                      {!collapsedGroups[group.state] && group.items.length > (groupLimits[group.state] || 50) && (
                        <tr>
                          <td colSpan={visibleColumns.length + 1 + (isManager ? 1 : 0)} className="!py-2 !text-center !border-b !border-slate-200">
                            <button onClick={(e) => { e.stopPropagation(); setGroupLimits(prev => ({ ...prev, [group.state]: (prev[group.state] || 50) + 50 })); }}
                              className="text-xs font-medium text-[#002FA7] hover:underline" data-testid={`load-more-${group.state}`}>
                              Show more ({group.items.length - (groupLimits[group.state] || 50)} remaining)
                            </button>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card Layout */}
            <div className="md:hidden" data-testid="planboard-mobile-list">
              {groupedItems.map((group) => (
                <div key={group.state}>
                  <div className="flex items-center gap-2 px-3 py-2.5 bg-slate-50 border-b border-slate-200 cursor-pointer select-none sticky top-0 z-10"
                    onClick={() => toggleGroup(group.state)} data-testid={`group-header-mobile-${group.state}`}>
                    {collapsedGroups[group.state] ? <ChevronRight size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: group.config.color }} />
                    <span className="text-sm font-semibold text-slate-700">{group.config.label}</span>
                    <span className="text-xs text-slate-400">({group.items.length})</span>
                  </div>
                  {!collapsedGroups[group.state] && (
                    <div className="divide-y divide-slate-100">
                      {group.items.slice(0, groupLimits[group.state] || 50).map((item) => {
                        const pCfg = getPriorityConfig(item.priority);
                        const _t3m = new Date(); const _today3m = `${_t3m.getFullYear()}-${String(_t3m.getMonth()+1).padStart(2,'0')}-${String(_t3m.getDate()).padStart(2,'0')}`;
                        const isOverdue = item.due_date && item.due_date.slice(0,10) < _today3m && !['done','released','cancelled'].includes(item.status);
                        return (
                          <div key={item.id} onClick={() => handleOpenDetail(item)}
                            className={`px-3 py-3 cursor-pointer active:bg-slate-50 transition-colors ${isOverdue ? 'bg-red-50/30' : 'bg-white'}`}
                            data-testid={`work-item-card-${item.id}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5 mb-0.5">
                                  <span className="text-slate-400 font-mono text-[10px]">{item.task_number}</span>
                                  {isOverdue && <span className="text-[9px] font-bold text-red-500 bg-red-100 px-1 rounded">OVERDUE</span>}
                                  {item.archived && <span className="text-[9px] font-bold text-amber-600 bg-amber-100 px-1 rounded">ARCHIVED</span>}
                                </div>
                                <p className="text-sm font-medium text-slate-800 leading-snug line-clamp-2">{item.title}</p>
                              </div>
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0" style={{ color: pCfg.color, background: `${pCfg.color}15` }}>{pCfg.label}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded truncate max-w-[140px]">{getProjectName(item)}</span>
                              {item.due_date && (
                                <span className={`text-[10px] flex items-center gap-0.5 ${isOverdue ? 'text-red-500 font-semibold' : 'text-slate-400'}`}>
                                  <Calendar size={9} /> {formatDate(item.due_date)}
                                </span>
                              )}
                              <div className="flex -space-x-1.5 ml-auto">
                                {(item.assignees || []).slice(0, 3).map((a, i) => (
                                  <div key={i} className="w-5 h-5 rounded-full bg-[#002FA7]/10 border border-white flex items-center justify-center" title={a.name}>
                                    <span className="text-[8px] font-bold text-[#002FA7]">{(a.name || '?')[0]?.toUpperCase()}</span>
                                  </div>
                                ))}
                                {(item.assignees || []).length > 3 && <span className="text-[9px] text-slate-400 ml-1">+{(item.assignees || []).length - 3}</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {group.items.length > (groupLimits[group.state] || 50) && (
                        <div className="py-2 text-center">
                          <button onClick={(e) => { e.stopPropagation(); setGroupLimits(prev => ({ ...prev, [group.state]: (prev[group.state] || 50) + 50 })); }}
                            className="text-xs font-medium text-[#002FA7]" data-testid={`load-more-mobile-${group.state}`}>
                            Show more ({group.items.length - (groupLimits[group.state] || 50)} remaining)
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          /* ===== BOARD VIEW ===== */
          <div className="flex gap-3 sm:gap-4 p-3 sm:p-4 h-full overflow-x-auto snap-x snap-mandatory sm:snap-none" data-testid="kanban-board">
            {boardColumns.map(col => (
              <div key={col.status} className="flex-shrink-0 w-[calc(100vw-4.5rem)] sm:w-72 flex flex-col bg-slate-50 rounded-lg border border-slate-200 snap-start" data-testid={`kanban-col-${col.status}`}>
                <div className="px-3 py-2.5 border-b border-slate-200 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: col.config.color }} />
                  <span className="text-sm font-semibold text-slate-700">{col.config.label}</span>
                  <span className="text-xs text-slate-400 ml-auto bg-white rounded-full px-2">{col.tasks.length}</span>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-2">
                  {col.tasks.map(item => {
                    const pCfg = getPriorityConfig(item.priority);
                    const _t4 = new Date(); const _today4 = `${_t4.getFullYear()}-${String(_t4.getMonth()+1).padStart(2,'0')}-${String(_t4.getDate()).padStart(2,'0')}`;
                    const isOverdue = item.due_date && item.due_date.slice(0,10) < _today4 && !['done','released','cancelled'].includes(item.status);
                    return (
                      <div key={item.id} onClick={() => handleOpenDetail(item)}
                        className={`bg-white rounded-md border p-3 cursor-pointer hover:border-[#002FA7]/40 hover:shadow-sm transition-all ${isOverdue ? 'border-red-200' : 'border-slate-200'}`}
                        data-testid={`kanban-card-${item.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <span className="text-xs text-slate-400 font-mono">{item.task_number}</span>
                          <span className="text-[10px] font-bold px-1.5 rounded" style={{ color: pCfg.color, background: `${pCfg.color}15` }}>{pCfg.label}</span>
                        </div>
                        <p className="text-sm font-medium text-slate-800 mb-2 line-clamp-2">{item.title}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{getProjectName(item)}</span>
                          <div className="flex items-center gap-1.5">
                            {item.due_date && <span className={`text-[10px] ${isOverdue ? 'text-red-500 font-semibold' : 'text-slate-400'}`}>{formatDate(item.due_date)}</span>}
                            <div className="flex -space-x-1.5">
                              {(item.assignees || []).slice(0, 2).map((a, i) => (
                                <div key={i} className="w-5 h-5 rounded-full bg-[#002FA7]/10 border border-white flex items-center justify-center">
                                  <span className="text-[8px] font-bold text-[#002FA7]">{getInitials(a.name)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {col.tasks.length === 0 && <div className="text-center text-xs text-slate-300 py-8">No tasks</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ===== SELECTION ACTION BAR ===== */}
      {isManager && selectedTasks.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white rounded-xl shadow-2xl px-5 py-3 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-4" data-testid="selection-action-bar">
          <span className="text-sm font-medium">{selectedTasks.length} task{selectedTasks.length > 1 ? 's' : ''} selected</span>
          <div className="w-px h-5 bg-slate-600" />
          {showArchived ? (
            <button onClick={async () => {
              try {
                await Promise.all(selectedTasks.map(id => taskAPI.unarchiveTask(id)));
                toast.success(`${selectedTasks.length} task${selectedTasks.length > 1 ? 's' : ''} unarchived`);
                setSelectedTasks([]);
                fetchWorkItems();
              } catch (err) { toast.error('Unarchive failed'); }
            }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-600 hover:bg-emerald-500 transition-colors" data-testid="unarchive-selected-btn">
              <ArchiveRestore size={14} /> Unarchive
            </button>
          ) : (
            <button onClick={async () => {
              try {
                await Promise.all(selectedTasks.map(id => taskAPI.archiveTask(id)));
                toast.success(`${selectedTasks.length} task${selectedTasks.length > 1 ? 's' : ''} archived`);
                setSelectedTasks([]);
                fetchWorkItems();
              } catch (err) { toast.error('Archive failed'); }
            }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-amber-600 hover:bg-amber-500 transition-colors" data-testid="archive-selected-btn">
              <Archive size={14} /> Archive
            </button>
          )}
          <button onClick={() => setSelectedTasks([])} className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors" data-testid="deselect-all-btn">
            <X size={16} />
          </button>
        </div>
      )}

      {/* ===== CREATE DIALOG ===== */}
      <Dialog open={createDialogOpen} onOpenChange={(open) => { setCreateDialogOpen(open); if (!open) { setNewTask(emptyTask); setAiSuggestions(null); setAiEnhanceChanges([]); } }}>
        <DialogContent className="sm:max-w-[640px] p-0 border border-slate-200 bg-white max-h-[100vh] sm:max-h-[85vh] overflow-hidden flex flex-col" data-testid="create-work-item-dialog">
          <div className="px-4 sm:px-6 pt-5 pb-3 border-b border-slate-100 flex-shrink-0">
            <div className="flex items-center justify-between">
              <DialogHeader><DialogTitle className="font-heading text-base sm:text-lg font-bold text-slate-900">Create Work Item</DialogTitle></DialogHeader>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <button onClick={handleEnhanceTask} disabled={aiEnhancing || (!newTask.title.trim() && !newTask.description?.trim())}
                  className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-[10px] sm:text-xs font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 transition-colors" data-testid="ai-enhance-btn">
                  {aiEnhancing ? <Loader2 size={12} className="animate-spin" /> : <Edit2 size={12} />} AI Enhance
                </button>
                <button onClick={handleAiSuggest} disabled={aiSugLoading || !newTask.title.trim()}
                  className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-[10px] sm:text-xs font-medium bg-purple-50 text-purple-700 hover:bg-purple-100 disabled:opacity-50 transition-colors" data-testid="ai-suggest-btn">
                  {aiSugLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} AI Suggest
                </button>
              </div>
            </div>
          </div>
          <div className="px-4 sm:px-6 py-4 space-y-4 overflow-y-auto flex-1">
            {aiEnhanceChanges.length > 0 && (
              <div className="p-3 rounded-md bg-emerald-50 border border-emerald-100" data-testid="ai-enhance-changes">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-mono text-[10px] text-emerald-600 uppercase tracking-wider flex items-center gap-1"><Edit2 size={10} /> Writing Improvements</span>
                  <button onClick={() => setAiEnhanceChanges([])} className="text-[10px] text-emerald-500 hover:underline">Dismiss</button>
                </div>
                <ul className="space-y-0.5">
                  {aiEnhanceChanges.map((c, i) => <li key={i} className="text-xs text-emerald-700 flex items-start gap-1.5"><Check size={12} className="mt-0.5 flex-shrink-0" />{c}</li>)}
                </ul>
              </div>
            )}
            {aiSuggestions && (
              <div className="p-3 rounded-md bg-purple-50 border border-purple-100" data-testid="ai-suggestions-panel">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[10px] text-purple-600 uppercase tracking-wider flex items-center gap-1"><Sparkles size={10} /> AI Suggestions</span>
                  <button onClick={applyAiSuggestions} className="text-[10px] font-semibold text-purple-700 hover:underline">Apply</button>
                </div>
                <p className="text-xs text-purple-700">Priority: <strong>{aiSuggestions.priority}</strong> | Labels: <strong>{aiSuggestions.labels?.join(', ') || 'none'}</strong></p>
                {aiSuggestions.suggestion && <p className="text-xs text-purple-600 mt-1">{aiSuggestions.suggestion}</p>}
              </div>
            )}
            <div>
              <label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Task Type *</label>
              <div className="flex gap-2" data-testid="task-type-selector">
                {['regular', 'support', 'meeting', 'training'].map(tt => (
                  <button key={tt} type="button" onClick={() => setNewTask(t => ({ ...t, task_type: tt }))}
                    className={`px-4 py-2 rounded-md text-sm font-medium border transition-all ${newTask.task_type === tt ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                    data-testid={`task-type-${tt}`}>
                    {tt.charAt(0).toUpperCase() + tt.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Title *</label>
              <input type="text" placeholder="What needs to be done?" value={newTask.title} onChange={(e) => setNewTask(t => ({ ...t, title: e.target.value }))}
                className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/10" data-testid="new-task-title" autoFocus />
            </div>
            {newTask.task_type === 'regular' && <div>
              <label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Description <span className="text-red-500">*</span></label>
              <textarea placeholder="Details..." value={newTask.description} onChange={(e) => setNewTask(t => ({ ...t, description: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm outline-none resize-y focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/10 min-h-[60px] max-h-[200px]" rows={3} data-testid="new-task-description" />
            </div>}
            <div className={`grid gap-3 ${newTask.task_type === 'regular' ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-1'}`}>
              {newTask.task_type === 'regular' && <>
              <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Status</label>
                <select value={newTask.status} onChange={(e) => setNewTask(t => ({ ...t, status: e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none bg-white" data-testid="new-task-status">
                  {Object.entries(statusConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></div>
              <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Priority</label>
                <select value={newTask.priority} onChange={(e) => setNewTask(t => ({ ...t, priority: e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none bg-white" data-testid="new-task-priority">
                  {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></div>
              </>}
              <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Project *</label>
                <select value={newTask.client_id || (selectedProject !== 'all' ? selectedProject : '')} onChange={(e) => handleProjectChange(e.target.value, setNewTask)}
                  className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none bg-white" data-testid="new-task-project">
                  <option value="">Select project</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}{p.client?.name ? ` (${p.client.name})` : ''}</option>)}</select></div>
            </div>
            {isManager ? (
            <div className="relative">
              <label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Assignees *</label>
              <div className="flex flex-wrap gap-1 p-2 rounded-md border border-slate-200 min-h-[36px] cursor-pointer"
                onClick={() => setAssigneeDropdownOpen(assigneeDropdownOpen === 'new' ? null : 'new')} data-testid="new-task-assignees">
                {newTask.assignees.length > 0 ? newTask.assignees.map(uid => {
                  const u = users.find(x => x.id === uid);
                  return u ? <span key={uid} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#002FA7]/10 text-[#002FA7] border border-[#002FA7]/20">{u.name}<X size={10} className="cursor-pointer" onClick={(e) => { e.stopPropagation(); toggleAssignee(uid, setNewTask); }} /></span> : null;
                }) : <span className="text-xs text-slate-400">Select assignees...</span>}
              </div>
              {assigneeDropdownOpen === 'new' && (
                <div className="absolute z-30 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  <input type="text" placeholder="Search..." value={assigneeSearch} onChange={(e) => setAssigneeSearch(e.target.value)} className="w-full px-3 py-2 text-sm border-b border-slate-100 outline-none sticky top-0 bg-white" autoFocus onClick={(e) => e.stopPropagation()} />
                  {users.filter(u => !assigneeSearch || u.name.toLowerCase().includes(assigneeSearch.toLowerCase())).map(u => (
                    <div key={u.id} onClick={(e) => { e.stopPropagation(); toggleAssignee(u.id, setNewTask); }} className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${newTask.assignees.includes(u.id) ? 'bg-blue-50' : ''}`}>
                      <span className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-[9px] font-bold text-slate-600">{getInitials(u.name)}</span>
                      <span className="flex-1 text-slate-700">{u.name}</span>
                      {newTask.assignees.includes(u.id) && <Check size={14} className="text-[#002FA7]" />}
                    </div>
                  ))}
                </div>
              )}
            </div>
            ) : (
            <div className="p-2.5 rounded-md bg-blue-50 border border-blue-100">
              <p className="text-xs text-blue-700">This task will be automatically assigned to the Project Manager for review.</p>
            </div>
            )}
            {newTask.task_type === 'regular' && <div className="grid grid-cols-2 gap-3">
              <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Module</label>
                <select value={newTask.module || ''} onChange={(e) => setNewTask(t => ({ ...t, module: e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none bg-white" data-testid="new-task-module">
                  <option value="">Select module</option>{masterModules.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}</select></div>
              <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Estimated</label>
                <input type="text" value={newTask.estimated || ''} onChange={(e) => setNewTask(t => ({ ...t, estimated: e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none" placeholder="e.g. 4h" data-testid="new-task-estimated" /></div>
            </div>}
            {newTask.task_type === 'regular' && <div className="grid grid-cols-2 gap-3">
              <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Sprint <span className="text-red-500">*</span></label>
                <select value={newTask.sprint_id || ''} onChange={(e) => {
                  const selected = sprints.find(s => s.id === e.target.value);
                  setNewTask(t => ({ ...t, cycle: selected?.name || '', sprint_id: e.target.value || null }));
                }} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none bg-white" data-testid="new-task-sprint">
                  <option value="">Select sprint</option>{sprints.filter(s => s.status !== 'completed').map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
              <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Use Case <span className="text-red-500">*</span></label>
                <select value={newTask.use_case || ''} onChange={(e) => setNewTask(t => ({ ...t, use_case: e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none bg-white" data-testid="new-task-use-case">
                  <option value="">Select use case</option>{USE_CASE_OPTIONS.map(uc => <option key={uc} value={uc}>{uc}</option>)}</select></div>
              <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Functional Area <span className="text-red-500">*</span></label>
                <select value={newTask.functional_area || ''} onChange={(e) => setNewTask(t => ({ ...t, functional_area: e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none bg-white" data-testid="new-task-functional-area">
                  <option value="">Select functional area</option>{FUNCTIONAL_AREA_OPTIONS.map(fa => <option key={fa} value={fa}>{fa}</option>)}</select></div>
            </div>}
            <div className="grid grid-cols-2 gap-3">
              <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Start Date{newTask.task_type !== 'regular' ? ' *' : ''}</label>
                <input type="date" value={newTask.start_date} onChange={(e) => setNewTask(t => ({ ...t, start_date: e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none" data-testid="new-task-start-date" /></div>
              <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Due Date *</label>
                <input type="date" value={newTask.due_date} onChange={(e) => setNewTask(t => ({ ...t, due_date: e.target.value, targeted_release_date: t.targeted_release_date || e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none" data-testid="new-task-due-date" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Estimated Hours *</label>
                <input type="number" min="0" step="0.01" placeholder="e.g. 4" value={newTask.estimated_hours} onChange={(e) => setNewTask(t => ({ ...t, estimated_hours: e.target.value }))}
                  className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none" data-testid="new-task-estimated-hours" />
                <p className="text-[10px] text-slate-400 mt-0.5">e.g. 0.25 = 15min, 0.5 = 30min, 1.5 = 1h 30min</p></div>
              <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Actual Hours</label>
                <input type="number" min="0" step="0.01" placeholder="e.g. 3" value={newTask.actual_hours} onChange={(e) => setNewTask(t => ({ ...t, actual_hours: e.target.value }))}
                  className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none" data-testid="new-task-actual-hours" /></div>
            </div>
            {newTask.task_type === 'regular' && <>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Targeted Release Date</label>
                <input type="date" value={newTask.targeted_release_date || ''} onChange={(e) => setNewTask(t => ({ ...t, targeted_release_date: e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none" data-testid="new-task-targeted-release-date" /></div>
              <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Release Date</label>
                <input type="date" value={newTask.release_date || ''} onChange={(e) => setNewTask(t => ({ ...t, release_date: e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none" data-testid="new-task-release-date" /></div>
            </div>
            <div className="relative"><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Labels <span className="text-red-500">*</span></label>
              <div className="flex flex-wrap gap-1 p-2 rounded-md border border-slate-200 min-h-[36px] cursor-pointer" onClick={() => setLabelDropdownOpen(labelDropdownOpen === 'new' ? null : 'new')} data-testid="new-task-labels">
                {newTask.labels.length > 0 ? newTask.labels.map(l => (
                  <span key={l} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600">{l}<X size={10} className="cursor-pointer" onClick={(e) => { e.stopPropagation(); setNewTask(t => ({ ...t, labels: t.labels.filter(x => x !== l) })); }} /></span>
                )) : <span className="text-xs text-slate-400">Select labels...</span>}
              </div>
              {labelDropdownOpen === 'new' && (
                <div className="absolute z-30 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {masterLabels.map(ml => (
                    <div key={ml.id} onClick={(e) => { e.stopPropagation(); setNewTask(t => ({ ...t, labels: t.labels.includes(ml.name) ? t.labels.filter(l => l !== ml.name) : [...t.labels, ml.name] })); }}
                      className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${newTask.labels.includes(ml.name) ? 'bg-blue-50' : ''}`}>
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: ml.color || '#6b7280' }} /><span className="flex-1 text-slate-700">{ml.name}</span>
                      {newTask.labels.includes(ml.name) && <Check size={14} className="text-[#002FA7]" />}
                    </div>
                  ))}
                  {masterLabels.length === 0 && <p className="px-3 py-2 text-xs text-slate-400">No labels. Add in Settings.</p>}
                </div>
              )}
            </div>
            </>}
          </div>
          <div className="px-6 py-3 flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 flex-shrink-0">
            <button className="px-4 py-2 rounded-md text-sm text-slate-600 border border-slate-200 bg-white hover:bg-slate-50" onClick={() => { setCreateDialogOpen(false); setNewTask(emptyTask); setAiSuggestions(null); setAiEnhanceChanges([]); }} data-testid="create-discard-btn">Discard</button>
            <button className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-[#002FA7] hover:bg-[#002585] flex items-center gap-1.5 disabled:opacity-50" onClick={handleCreateTask} disabled={creating || !newTask.title.trim()} data-testid="create-save-btn">
              {creating && <Loader2 size={14} className="animate-spin" />} Create
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== CREATE ACTIVITY DIALOG ===== */}
      <Dialog open={createActivityOpen} onOpenChange={(open) => { setCreateActivityOpen(open); if (!open) { setNewActivity(emptyActivity); setActivityEnhanceChanges([]); } }}>
        <DialogContent className="sm:max-w-[480px] p-0 border border-slate-200 bg-white max-h-[100vh] sm:max-h-[85vh] overflow-hidden flex flex-col" data-testid="create-activity-dialog">
          <div className="px-4 sm:px-6 pt-5 pb-3 flex items-center justify-between">
            <DialogHeader><DialogTitle className="font-heading text-base sm:text-lg font-bold text-slate-900">Log Activity</DialogTitle></DialogHeader>
            <button onClick={handleEnhanceActivity} disabled={activityEnhancing || (!newActivity.title.trim() && !newActivity.description?.trim())}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 transition-colors" data-testid="ai-enhance-activity-btn">
              {activityEnhancing ? <Loader2 size={12} className="animate-spin" /> : <Edit2 size={12} />} AI Enhance
            </button>
          </div>
          <div className="px-6 pb-5 space-y-4 max-h-[60vh] overflow-y-auto">
            {activityEnhanceChanges.length > 0 && (
              <div className="p-3 rounded-md bg-emerald-50 border border-emerald-100" data-testid="ai-enhance-activity-changes">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-mono text-[10px] text-emerald-600 uppercase tracking-wider flex items-center gap-1"><Edit2 size={10} /> Writing Improvements</span>
                  <button onClick={() => setActivityEnhanceChanges([])} className="text-[10px] text-emerald-500 hover:underline">Dismiss</button>
                </div>
                <ul className="space-y-0.5">
                  {activityEnhanceChanges.map((c, i) => <li key={i} className="text-xs text-emerald-700 flex items-start gap-1.5"><Check size={12} className="mt-0.5 flex-shrink-0" />{c}</li>)}
                </ul>
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Title <span className="text-red-500">*</span></label>
              <input value={newActivity.title} onChange={(e) => setNewActivity(p => ({ ...p, title: e.target.value }))} placeholder="e.g., Client meeting, Product training..." className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:border-[#002FA7]" data-testid="activity-title-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Type <span className="text-red-500">*</span></label>
              <select value={newActivity.task_type} onChange={(e) => setNewActivity(p => ({ ...p, task_type: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:border-[#002FA7]" data-testid="activity-type-select">
                <option value="meeting">Meeting</option>
                <option value="training">Training</option>
                <option value="support">Support</option>
              </select>
            </div>
            <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Project <span className="text-red-500">*</span></label>
                <select value={newActivity.client_id} onChange={(e) => setNewActivity(p => ({ ...p, client_id: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:border-[#002FA7]" data-testid="activity-project-select">
                  <option value="">Select project</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Date <span className="text-red-500">*</span></label>
                <input type="date" value={newActivity.start_date} onChange={(e) => setNewActivity(p => ({ ...p, start_date: e.target.value, due_date: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:border-[#002FA7]" data-testid="activity-date-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Est. Hours <span className="text-red-500">*</span></label>
                <input type="number" step="0.01" value={newActivity.estimated_hours} onChange={(e) => setNewActivity(p => ({ ...p, estimated_hours: e.target.value }))} placeholder="e.g., 0.5 = 30min" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:border-[#002FA7]" data-testid="activity-hours-input" />
                <p className="text-[10px] text-slate-400 mt-0.5">e.g. 0.25 = 15min, 0.5 = 30min, 1.5 = 1h 30min</p>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Description <span className="text-red-500">*</span></label>
              <textarea value={newActivity.description} onChange={(e) => setNewActivity(p => ({ ...p, description: e.target.value }))} rows={3} placeholder="Brief description..." className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:border-[#002FA7] resize-none" data-testid="activity-desc-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">Assignees</label>
              <div className="flex flex-wrap gap-1.5" data-testid="activity-assignees-picker">
                {users.map(u => {
                  const sel = newActivity.assignees?.includes(u.id);
                  return <button key={u.id} type="button" onClick={() => setNewActivity(p => ({ ...p, assignees: sel ? p.assignees.filter(id => id !== u.id) : [...(p.assignees || []), u.id] }))}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${sel ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}
                    data-testid={`activity-assignee-${u.id}`}>{u.name}</button>;
                })}
              </div>
            </div>
          </div>
          <div className="px-6 py-3 border-t border-slate-200 flex justify-end gap-2">
            <button className="px-4 py-2 rounded-md text-sm text-slate-600 border border-slate-200 bg-white hover:bg-slate-50" onClick={() => { setCreateActivityOpen(false); setNewActivity(emptyActivity); setActivityEnhanceChanges([]); }} data-testid="activity-discard-btn">Cancel</button>
            <button className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-[#002FA7] hover:bg-[#002585] flex items-center gap-1.5 disabled:opacity-50" onClick={handleCreateActivity} disabled={creating || !newActivity.title.trim()} data-testid="activity-save-btn">
              {creating && <Loader2 size={14} className="animate-spin" />} Submit
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== TASK DETAIL DIALOG ===== */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-[700px] p-0 border border-slate-200 bg-white max-h-[100vh] sm:max-h-[88vh] overflow-hidden flex flex-col" data-testid="task-detail-dialog">
          {editingTask && (
            <>
              <div className="px-3 sm:px-6 pt-5 pb-3 border-b border-slate-100 flex-shrink-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded">{editingTask.task_number}</span>
                  <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded truncate">{getProjectName(editingTask)}</span>
                </div>
                <h2 className="text-sm font-semibold text-slate-800 mt-1.5 mb-2 line-clamp-2 sm:truncate" title={editingTask.title} data-testid="detail-task-title">{editingTask.title}</h2>
                <div className="flex items-center gap-1 mt-3 overflow-x-auto -mx-1 px-1 scrollbar-hide" data-testid="detail-tabs">
                  {[
                    { key: 'info', label: 'Details', icon: FolderOpen },
                    { key: 'subtasks', label: 'Subtasks', icon: ListChecks },
                    { key: 'attachments', label: 'Files', icon: Paperclip },
                    { key: 'comments', label: 'Discussion', icon: MessageSquare },
                    { key: 'history', label: 'History', icon: History },
                  ].map(t => (
                    <button key={t.key} onClick={() => setDetailTab(t.key)}
                      className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${detailTab === t.key ? 'bg-slate-100 text-slate-800' : 'text-slate-400 hover:text-slate-600'}`} data-testid={`tab-${t.key}`}>
                      <t.icon size={13} /> <span className="hidden sm:inline">{t.label}</span><span className="sm:hidden">{t.key === 'comments' ? 'Chat' : t.key === 'attachments' ? 'Files' : t.label}</span>
                      {t.key === 'subtasks' && subtasks.length > 0 && <span className="bg-[#002FA7] text-white rounded-full px-1.5 text-[9px] font-bold">{subtasks.length}</span>}
                      {t.key === 'comments' && comments.length > 0 && <span className="bg-[#002FA7] text-white rounded-full px-1.5 text-[9px] font-bold">{comments.length}</span>}
                      {t.key === 'attachments' && attachments.length > 0 && <span className="bg-slate-500 text-white rounded-full px-1.5 text-[9px] font-bold">{attachments.length}</span>}
                      {t.key === 'history' && history.length > 0 && <span className="bg-slate-400 text-white rounded-full px-1.5 text-[9px] font-bold">{history.length}</span>}
                    </button>
                  ))}
                  <div className="ml-auto flex-shrink-0">
                    <button onClick={handleShare} className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-500 hover:text-[#002FA7] hover:bg-slate-100 rounded transition-colors whitespace-nowrap" data-testid="share-task-btn"><Share2 size={13} /> <span className="hidden sm:inline">Share</span></button>
                  </div>
                </div>
                {shareUrl && (
                  <div className="mt-2 flex items-center gap-2 px-2 py-1.5 bg-green-50 border border-green-200 rounded-md">
                    <Link2 size={12} className="text-green-600" />
                    <span className="text-xs text-green-700 flex-1 truncate">{shareUrl}</span>
                    <button onClick={() => navigator.clipboard.writeText(shareUrl)} className="text-[10px] font-semibold text-green-700 hover:underline">Copy</button>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto">
                {/* INFO TAB */}
                {detailTab === 'info' && (
                  <div className="px-3 sm:px-6 py-4 space-y-4">
                    <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Title</label>
                      <input type="text" value={editingTask.title} onChange={(e) => setEditingTask(t => ({ ...t, title: e.target.value }))} className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm outline-none focus:border-[#002FA7]" data-testid="edit-task-title" /></div>
                    <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Description</label>
                      <textarea value={editingTask.description || ''} onChange={(e) => setEditingTask(t => ({ ...t, description: e.target.value }))}
                        className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm outline-none resize-y focus:border-[#002FA7] min-h-[80px] max-h-[250px]" rows={4} data-testid="edit-task-description" /></div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Status</label>
                        <select value={editingTask.status} onChange={(e) => setEditingTask(t => ({ ...t, status: e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm bg-white outline-none" data-testid="edit-task-status">
                          {Object.entries(statusConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></div>
                      <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Priority</label>
                        <select value={editingTask.priority} onChange={(e) => setEditingTask(t => ({ ...t, priority: e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm bg-white outline-none" data-testid="edit-task-priority">
                          {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></div>
                      <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Project</label>
                        <select value={editingTask.client_id || editingTask.project?.id || ''} onChange={(e) => handleProjectChange(e.target.value, setEditingTask)} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm bg-white outline-none" data-testid="edit-task-project">
                          <option value="">Select project</option>
                          {projects.map(p => <option key={p.id} value={p.id}>{p.name}{p.client?.name ? ` (${p.client.name})` : ''}</option>)}</select></div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Business</label>
                        <input type="text" value={editingTask.business || ''} onChange={(e) => setEditingTask(t => ({ ...t, business: e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none" placeholder="Business unit" data-testid="edit-task-business" /></div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Estimated Hours *</label>
                        <input type="number" min="0" step="0.01" value={editingTask.estimated_hours || ''} onChange={(e) => setEditingTask(t => ({ ...t, estimated_hours: e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none" placeholder="e.g. 4" data-testid="edit-task-estimated-hours" required />
                        <p className="text-[10px] text-slate-400 mt-0.5">0.25 = 15min, 0.5 = 30min</p></div>
                      <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Actual Hours</label>
                        <input type="number" min="0" step="0.01" value={editingTask.actual_hours || ''} onChange={(e) => setEditingTask(t => ({ ...t, actual_hours: e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none" placeholder="e.g. 3" data-testid="edit-task-actual-hours" />
                        <p className="text-[10px] text-slate-400 mt-0.5">0.25 = 15min, 0.5 = 30min</p></div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Use Case</label>
                        <select value={editingTask.use_case || ''} onChange={(e) => setEditingTask(t => ({ ...t, use_case: e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none bg-white" data-testid="edit-task-use-case">
                          <option value="">Select use case</option>{USE_CASE_OPTIONS.map(uc => <option key={uc} value={uc}>{uc}</option>)}</select></div>
                      <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Functional Area</label>
                        <select value={editingTask.functional_area || ''} onChange={(e) => setEditingTask(t => ({ ...t, functional_area: e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none bg-white" data-testid="edit-task-functional-area">
                          <option value="">Select functional area</option>{FUNCTIONAL_AREA_OPTIONS.map(fa => <option key={fa} value={fa}>{fa}</option>)}</select></div>
                    </div>
                    <div className="relative"><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Assignees</label>
                      {isManager ? (
                      <div className="flex flex-wrap gap-1 p-2 rounded-md border border-slate-200 min-h-[36px] cursor-pointer" onClick={() => setAssigneeDropdownOpen(assigneeDropdownOpen === 'edit' ? null : 'edit')} data-testid="edit-task-assignees">
                        {editingTask.assignees?.length > 0 ? editingTask.assignees.map(uid => {
                          const u = users.find(x => x.id === uid);
                          return u ? <span key={uid} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#002FA7]/10 text-[#002FA7] border border-[#002FA7]/20">{u.name}<X size={10} className="cursor-pointer" onClick={(e) => { e.stopPropagation(); toggleAssignee(uid, setEditingTask); }} /></span> : null;
                        }) : <span className="text-xs text-slate-400">Select assignees...</span>}
                      </div>
                      ) : (
                      <div className="flex flex-wrap gap-1 p-2 rounded-md border border-slate-100 bg-slate-50 min-h-[36px]">
                        {editingTask.assignees?.length > 0 ? editingTask.assignees.map(uid => {
                          const u = users.find(x => x.id === uid);
                          return u ? <span key={uid} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#002FA7]/10 text-[#002FA7] border border-[#002FA7]/20">{u.name}</span> : null;
                        }) : <span className="text-xs text-slate-400">No assignees</span>}
                      </div>
                      )}
                      {isManager && assigneeDropdownOpen === 'edit' && (
                        <div className="absolute z-30 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          <input type="text" placeholder="Search..." value={assigneeSearch} onChange={(e) => setAssigneeSearch(e.target.value)} className="w-full px-3 py-2 text-sm border-b border-slate-100 outline-none sticky top-0 bg-white" autoFocus onClick={(e) => e.stopPropagation()} />
                          {users.filter(u => !assigneeSearch || u.name.toLowerCase().includes(assigneeSearch.toLowerCase())).map(u => (
                            <div key={u.id} onClick={(e) => { e.stopPropagation(); toggleAssignee(u.id, setEditingTask); }} className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${editingTask.assignees?.includes(u.id) ? 'bg-blue-50' : ''}`}>
                              <span className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-[9px] font-bold text-slate-600">{getInitials(u.name)}</span><span className="flex-1 text-slate-700">{u.name}</span>
                              {editingTask.assignees?.includes(u.id) && <Check size={14} className="text-[#002FA7]" />}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Start Date</label>
                        <input type="date" value={editingTask.start_date || ''} onChange={(e) => setEditingTask(t => ({ ...t, start_date: e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none" data-testid="edit-task-start-date" /></div>
                      <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Due Date</label>
                        <input type="date" value={editingTask.due_date || ''} onChange={(e) => setEditingTask(t => ({ ...t, due_date: e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none" data-testid="edit-task-due-date" /></div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Release Date</label>
                        <input type="date" value={editingTask.release_date || ''} onChange={(e) => setEditingTask(t => ({ ...t, release_date: e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none" data-testid="edit-task-release-date" /></div>
                      <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Targeted Release Date</label>
                        <input type="date" value={editingTask.targeted_release_date || ''} onChange={(e) => setEditingTask(t => ({ ...t, targeted_release_date: e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none" data-testid="edit-task-targeted-release-date" /></div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Module</label>
                        <select value={editingTask.module || ''} onChange={(e) => setEditingTask(t => ({ ...t, module: e.target.value }))} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none bg-white" data-testid="edit-task-module">
                          <option value="">Select module</option>{masterModules.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}</select></div>
                      <div><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Sprint <span className="text-red-500">*</span></label>
                        <select value={editingTask.sprint_id || ''} onChange={(e) => {
                          const selected = sprints.find(s => s.id === e.target.value);
                          setEditingTask(t => ({ ...t, sprint_id: e.target.value || null, cycle: selected?.name || '' }));
                        }} className="w-full h-9 px-2.5 rounded-md border border-slate-200 text-sm outline-none bg-white" data-testid="edit-task-cycle">
                          <option value="">Select sprint</option>{sprints.filter(s => s.status !== 'completed').map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
                    </div>
                    <div className="relative"><label className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 block mb-1.5">Labels</label>
                      <div className="flex flex-wrap gap-1 p-2 rounded-md border border-slate-200 min-h-[36px] cursor-pointer" onClick={() => setLabelDropdownOpen(labelDropdownOpen === 'edit' ? null : 'edit')} data-testid="edit-task-labels">
                        {(editingTask.labels || []).length > 0 ? editingTask.labels.map(l => (
                          <span key={l} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600">{l}<X size={10} className="cursor-pointer" onClick={(e) => { e.stopPropagation(); setEditingTask(t => ({ ...t, labels: t.labels.filter(x => x !== l) })); }} /></span>
                        )) : <span className="text-xs text-slate-400">Select labels...</span>}
                      </div>
                      {labelDropdownOpen === 'edit' && (
                        <div className="absolute z-30 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {masterLabels.map(ml => (
                            <div key={ml.id} onClick={(e) => { e.stopPropagation(); setEditingTask(t => ({ ...t, labels: (t.labels || []).includes(ml.name) ? t.labels.filter(l => l !== ml.name) : [...(t.labels || []), ml.name] })); }}
                              className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${(editingTask.labels || []).includes(ml.name) ? 'bg-blue-50' : ''}`}>
                              <span className="w-2.5 h-2.5 rounded-full" style={{ background: ml.color || '#6b7280' }} /><span className="flex-1 text-slate-700">{ml.name}</span>
                              {(editingTask.labels || []).includes(ml.name) && <Check size={14} className="text-[#002FA7]" />}
                            </div>
                          ))}
                          {masterLabels.length === 0 && <p className="px-3 py-2 text-xs text-slate-400">No labels. Add in Settings.</p>}
                        </div>
                      )}
                    </div>
                    <div className="pt-3 border-t border-slate-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-[10px] font-semibold text-purple-600 uppercase tracking-[0.15em] flex items-center gap-1"><Sparkles size={10} /> AI Summary</span>
                        <button onClick={handleAiSummary} disabled={aiLoading} className="text-[10px] font-medium text-purple-600 hover:underline flex items-center gap-1" data-testid="ai-summary-btn">
                          {aiLoading ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />} Generate</button>
                      </div>
                      {aiSummary && <p className="text-xs text-slate-600 bg-purple-50 p-3 rounded-md border border-purple-100" data-testid="ai-summary-text">{aiSummary}</p>}
                    </div>
                  </div>
                )}

                {/* SUBTASKS TAB */}
                {detailTab === 'subtasks' && (
                  <div className="px-3 sm:px-6 py-4">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs font-medium text-slate-500">{subtasks.length} subtask{subtasks.length !== 1 ? 's' : ''}</span>
                      <button onClick={() => setNewSubtask({ title: '', assignees: [], estimated_hours: '', actual_hours: '', due_date: editingTask?.due_date || '', status: 'todo' })}
                        className="px-3 py-1.5 text-xs font-medium bg-[#002FA7] text-white rounded-lg hover:bg-[#0035c0] flex items-center gap-1.5" data-testid="add-subtask-btn">
                        <Plus size={13} /> Add Subtask
                      </button>
                    </div>
                    {newSubtask && (
                      <div className="mb-4 p-3 border border-[#002FA7]/20 rounded-lg bg-[#002FA7]/5 space-y-2" data-testid="new-subtask-form">
                        <input type="text" placeholder="Subtask title" value={newSubtask.title} onChange={(e) => setNewSubtask(s => ({ ...s, title: e.target.value }))}
                          className="w-full h-8 px-2.5 text-sm border border-slate-200 rounded outline-none" autoFocus data-testid="subtask-title-input" />
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                          <div>
                            <label className="text-[10px] font-medium text-slate-500 block mb-0.5">Status</label>
                            <select value={newSubtask.status} onChange={(e) => setNewSubtask(s => ({ ...s, status: e.target.value }))} className="w-full h-8 px-2 text-xs border border-slate-200 rounded bg-white outline-none">
                              {Object.entries(statusConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-slate-500 block mb-0.5">Est. Hours *</label>
                            <input type="number" min="0" step="0.01" placeholder="0" value={newSubtask.estimated_hours} onChange={(e) => setNewSubtask(s => ({ ...s, estimated_hours: e.target.value }))}
                              className="w-full h-8 px-2 text-xs border border-slate-200 rounded outline-none" data-testid="subtask-hours-input" required />
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-slate-500 block mb-0.5">Actual Hours</label>
                            <input type="number" min="0" step="0.01" placeholder="0" value={newSubtask.actual_hours} onChange={(e) => setNewSubtask(s => ({ ...s, actual_hours: e.target.value }))}
                              className="w-full h-8 px-2 text-xs border border-slate-200 rounded outline-none" data-testid="subtask-actual-hours-input" />
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-slate-500 block mb-0.5">Start Date</label>
                            <input type="date" value={newSubtask.start_date || ''} onChange={(e) => setNewSubtask(s => ({ ...s, start_date: e.target.value }))}
                              className="w-full h-8 px-2 text-xs border border-slate-200 rounded outline-none" data-testid="subtask-start-date-input" />
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-slate-500 block mb-0.5">Due Date</label>
                            <input type="date" value={newSubtask.due_date || ''} onChange={(e) => setNewSubtask(s => ({ ...s, due_date: e.target.value }))}
                              className="w-full h-8 px-2 text-xs border border-slate-200 rounded outline-none" data-testid="subtask-due-date-input" />
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          <label className="text-[10px] font-medium text-slate-500 w-full mb-0.5">Assignees</label>
                          {users.map(u => {
                            const sel = newSubtask.assignees?.includes(u.id);
                            return <button key={u.id} type="button" onClick={() => setNewSubtask(s => ({ ...s, assignees: sel ? s.assignees.filter(id => id !== u.id) : [...(s.assignees || []), u.id] }))}
                              className={`text-[10px] px-2 py-0.5 rounded-full border ${sel ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-500 border-slate-200'}`}
                              data-testid={`subtask-assignee-${u.id}`}>{u.name}</button>;
                          })}
                        </div>
                        <div className="flex gap-2 pt-1">
                          <button onClick={async () => {
                            if (!newSubtask.title.trim()) return;
                            if (!newSubtask.estimated_hours) { toast.error('Please enter Estimated Hours for subtask'); return; }
                            const newEst = newSubtask.estimated_hours ? parseFloat(newSubtask.estimated_hours) : 0;
                            const newAct = newSubtask.actual_hours ? parseFloat(newSubtask.actual_hours) : 0;
                            const sumEst = subtasks.reduce((s, st) => s + (st.estimated_hours || 0), 0);
                            const sumAct = subtasks.reduce((s, st) => s + (st.actual_hours || 0), 0);
                            const parentEst = editingTask?.estimated_hours || 0;
                            const parentAct = editingTask?.actual_hours || 0;
                            if (parentEst > 0 && (sumEst + newEst) > parentEst) {
                              toast.error(`Subtask estimated hours (${(sumEst + newEst).toFixed(2)}h) exceed parent's ${parentEst.toFixed(2)}h. Available: ${Math.max(0, parentEst - sumEst).toFixed(2)}h`);
                              return;
                            }
                            if (parentAct > 0 && (sumAct + newAct) > parentAct) {
                              toast.error(`Subtask actual hours (${(sumAct + newAct).toFixed(2)}h) exceed parent's ${parentAct.toFixed(2)}h. Available: ${Math.max(0, parentAct - sumAct).toFixed(2)}h`);
                              return;
                            }
                            try {
                              const res = await subtaskAPI.createSubtask(editingTask.id, { title: newSubtask.title, assignees: newSubtask.assignees, estimated_hours: newEst, actual_hours: newAct, start_date: newSubtask.start_date || null, due_date: newSubtask.due_date, status: newSubtask.status });
                              setSubtasks(prev => [...prev, res.data]);
                              setNewSubtask(null);
                            } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create subtask'); console.error(e); }
                          }} className="px-3 py-1 text-xs font-medium bg-[#002FA7] text-white rounded hover:bg-[#002585]" data-testid="save-subtask-btn">Save</button>
                          <button onClick={() => setNewSubtask(null)} className="px-3 py-1 text-xs text-slate-500 border border-slate-200 rounded hover:bg-slate-50">Cancel</button>
                        </div>
                      </div>
                    )}
                    {subtasks.length > 0 && (
                      <div className="space-y-1.5" data-testid="subtask-list">
                        {subtasks.map(st => {
                          const sCfg = statusConfig[st.status] || statusConfig.todo || DEFAULT_STATUS_CONFIG.todo;
                          if (editingSubtask?.id === st.id) {
                            return (
                              <div key={st.id} className="p-3 border border-[#002FA7]/20 rounded-lg bg-[#002FA7]/5 space-y-2" data-testid={`subtask-edit-${st.id}`}>
                                <input type="text" value={editingSubtask.title} onChange={(e) => setEditingSubtask(s => ({ ...s, title: e.target.value }))}
                                  className="w-full h-8 px-2.5 text-sm border border-slate-200 rounded outline-none" autoFocus data-testid="subtask-edit-title" />
                                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                                  <div>
                                    <label className="text-[10px] font-medium text-slate-500 block mb-0.5">Status</label>
                                    <select value={editingSubtask.status} onChange={(e) => setEditingSubtask(s => ({ ...s, status: e.target.value }))} className="w-full h-8 px-2 text-xs border border-slate-200 rounded bg-white outline-none">
                                      {Object.entries(statusConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="text-[10px] font-medium text-slate-500 block mb-0.5">Est. Hours *</label>
                                    <input type="number" min="0" step="0.01" placeholder="0" value={editingSubtask.estimated_hours || ''} onChange={(e) => setEditingSubtask(s => ({ ...s, estimated_hours: e.target.value }))}
                                      className="w-full h-8 px-2 text-xs border border-slate-200 rounded outline-none" data-testid="subtask-edit-hours" required />
                                  </div>
                                  <div>
                                    <label className="text-[10px] font-medium text-slate-500 block mb-0.5">Actual Hours</label>
                                    <input type="number" min="0" step="0.01" placeholder="0" value={editingSubtask.actual_hours || ''} onChange={(e) => setEditingSubtask(s => ({ ...s, actual_hours: e.target.value }))}
                                      className="w-full h-8 px-2 text-xs border border-slate-200 rounded outline-none" data-testid="subtask-edit-actual-hours" />
                                  </div>
                                  <div>
                                    <label className="text-[10px] font-medium text-slate-500 block mb-0.5">Start Date</label>
                                    <input type="date" value={editingSubtask.start_date || ''} onChange={(e) => setEditingSubtask(s => ({ ...s, start_date: e.target.value }))}
                                      className="w-full h-8 px-2 text-xs border border-slate-200 rounded outline-none" data-testid="subtask-edit-start-date" />
                                  </div>
                                  <div>
                                    <label className="text-[10px] font-medium text-slate-500 block mb-0.5">Due Date</label>
                                    <input type="date" value={editingSubtask.due_date || ''} onChange={(e) => setEditingSubtask(s => ({ ...s, due_date: e.target.value }))}
                                      className="w-full h-8 px-2 text-xs border border-slate-200 rounded outline-none" data-testid="subtask-edit-due-date" />
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  <label className="text-[10px] font-medium text-slate-500 w-full mb-0.5">Assignees</label>
                                    {users.slice(0, 10).map(u => {
                                      const sel = editingSubtask.assignees?.includes(u.id);
                                      return <button key={u.id} type="button" onClick={() => setEditingSubtask(s => ({ ...s, assignees: sel ? s.assignees.filter(id => id !== u.id) : [...(s.assignees || []), u.id] }))}
                                        className={`text-[10px] px-2 py-0.5 rounded-full border ${sel ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-500 border-slate-200'}`}
                                        title={u.name} data-testid={`subtask-edit-assignee-${u.id}`}>{u.name}</button>;
                                    })}
                                </div>
                                <div className="flex gap-2">
                                  <button onClick={async () => {
                                    if (!editingSubtask.title.trim()) return;
                                    if (!editingSubtask.estimated_hours) { toast.error('Please enter Estimated Hours for subtask'); return; }
                                    const newEst = editingSubtask.estimated_hours ? parseFloat(editingSubtask.estimated_hours) : 0;
                                    const sumEst = subtasks.filter(s => s.id !== st.id).reduce((s, x) => s + (x.estimated_hours || 0), 0);
                                    const parentEst = editingTask?.estimated_hours || 0;
                                    if (parentEst > 0 && (sumEst + newEst) > parentEst) {
                                      toast.error(`Subtask estimated hours (${(sumEst + newEst).toFixed(2)}h) exceed parent's ${parentEst.toFixed(2)}h. Available: ${Math.max(0, parentEst - sumEst).toFixed(2)}h`);
                                      return;
                                    }
                                    try {
                                      const newAct = editingSubtask.actual_hours ? parseFloat(editingSubtask.actual_hours) : 0;
                                      const sumAct = subtasks.filter(s => s.id !== st.id).reduce((s, x) => s + (x.actual_hours || 0), 0);
                                      const parentAct = editingTask?.actual_hours || 0;
                                      if (parentAct > 0 && (sumAct + newAct) > parentAct) {
                                        toast.error(`Subtask actual hours (${(sumAct + newAct).toFixed(2)}h) exceed parent's ${parentAct.toFixed(2)}h. Available: ${Math.max(0, parentAct - sumAct).toFixed(2)}h`);
                                        return;
                                      }
                                      await subtaskAPI.updateSubtask(st.id, {
                                        title: editingSubtask.title,
                                        status: editingSubtask.status,
                                        estimated_hours: newEst,
                                        actual_hours: newAct,
                                        start_date: editingSubtask.start_date || null,
                                        due_date: editingSubtask.due_date || null,
                                        assignees: editingSubtask.assignees || [],
                                      });
                                      setSubtasks(prev => prev.map(s => s.id === st.id ? { ...s, title: editingSubtask.title, status: editingSubtask.status, estimated_hours: newEst, actual_hours: newAct, start_date: editingSubtask.start_date, due_date: editingSubtask.due_date, assignees: editingSubtask.assignees.map(uid => { const u = users.find(x => x.id === uid); return u ? { id: u.id, name: u.name } : { id: uid, name: uid }; }) } : s));
                                      setEditingSubtask(null);
                                    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to update subtask'); console.error(err); }
                                  }} className="px-3 py-1 text-xs font-medium bg-[#002FA7] text-white rounded hover:bg-[#002585]" data-testid="subtask-save-edit-btn">Save</button>
                                  <button onClick={() => setEditingSubtask(null)} className="px-3 py-1 text-xs text-slate-500 border border-slate-200 rounded hover:bg-slate-50">Cancel</button>
                                </div>
                              </div>
                            );
                          }
                          return (
                            <React.Fragment key={st.id}>
                            {/* Desktop subtask row */}
                            <div className="hidden sm:flex items-center gap-3 p-2.5 rounded-lg border border-slate-100 hover:border-slate-200 group" data-testid={`subtask-${st.id}`}>
                              <select value={st.status} onChange={async (e) => {
                                const newStatus = e.target.value;
                                try {
                                  await subtaskAPI.updateSubtask(st.id, { status: newStatus });
                                  setSubtasks(prev => prev.map(s => s.id === st.id ? { ...s, status: newStatus } : s));
                                } catch (err) { console.error(err); }
                              }} className="h-6 px-1.5 text-[10px] font-medium border border-slate-200 rounded bg-white outline-none" style={{ color: sCfg.color }}>
                                {Object.entries(statusConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                              </select>
                              <div className="flex-1 min-w-0">
                                <span className="text-xs font-mono text-slate-400 mr-1.5">{st.subtask_number}</span>
                                <span className="text-sm text-slate-700">{st.title}</span>
                              </div>
                              {st.estimated_hours > 0 && <span className="text-[10px] text-slate-400 flex-shrink-0">{st.estimated_hours}h</span>}
                              {st.due_date && <span className="text-[10px] text-slate-400 flex-shrink-0 flex items-center gap-0.5"><Calendar className="h-3 w-3" />{new Date(st.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>}
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {(st.assignees || []).slice(0, 3).map(a => (
                                  <span key={a.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-slate-100 border border-slate-200" title={a.name}>
                                    <span className="w-4 h-4 rounded-full bg-[#002FA7]/10 text-[8px] font-bold text-[#002FA7] flex items-center justify-center">{a.name?.[0]?.toUpperCase()}</span>
                                    <span className="text-[10px] text-slate-600 font-medium max-w-[80px] truncate">{a.name}</span>
                                  </span>
                                ))}
                                {(st.assignees || []).length > 3 && (
                                  <span className="text-[10px] text-slate-400 font-medium" title={(st.assignees || []).slice(3).map(a => a.name).join(', ')}>+{(st.assignees || []).length - 3}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={async () => {
                                  if (subtaskHistory.id === st.id) { setSubtaskHistory({ id: null, entries: [] }); return; }
                                  try {
                                    const { data } = await subtaskAPI.getSubtaskHistory(st.id);
                                    setSubtaskHistory({ id: st.id, entries: data || [] });
                                  } catch (err) { console.error(err); }
                                }} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded" title="View history" data-testid={`subtask-history-btn-${st.id}`}>
                                  <History className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => setEditingSubtask({ id: st.id, title: st.title, status: st.status, estimated_hours: st.estimated_hours || '', actual_hours: st.actual_hours || '', start_date: st.start_date || '', due_date: st.due_date || '', assignees: (st.assignees || []).map(a => a.id || a) })}
                                  className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded" title="Edit subtask" data-testid={`subtask-edit-btn-${st.id}`}>
                                  <Edit2 className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={async () => {
                                  if (!window.confirm('Delete this subtask?')) return;
                                  try {
                                    await subtaskAPI.deleteSubtask(st.id);
                                    setSubtasks(prev => prev.filter(s => s.id !== st.id));
                                  } catch (err) { console.error(err); }
                                }} className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded" title="Delete subtask" data-testid={`subtask-delete-btn-${st.id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                            {/* Mobile subtask card */}
                            <div className="sm:hidden p-3 rounded-lg border border-slate-100" data-testid={`subtask-mobile-${st.id}`}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5 mb-0.5">
                                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: sCfg.color }} />
                                    <span className="text-[10px] font-mono text-slate-400">{st.subtask_number}</span>
                                  </div>
                                  <p className="text-sm text-slate-800 leading-snug">{st.title}</p>
                                </div>
                                <div className="flex items-center gap-0.5 flex-shrink-0">
                                  <button onClick={async () => {
                                    if (subtaskHistory.id === st.id) { setSubtaskHistory({ id: null, entries: [] }); return; }
                                    try { const { data } = await subtaskAPI.getSubtaskHistory(st.id); setSubtaskHistory({ id: st.id, entries: data || [] }); } catch (err) { console.error(err); }
                                  }} className="p-1.5 text-slate-400 active:bg-slate-100 rounded">
                                    <History className="h-3.5 w-3.5" />
                                  </button>
                                  <button onClick={() => setEditingSubtask({ id: st.id, title: st.title, status: st.status, estimated_hours: st.estimated_hours || '', actual_hours: st.actual_hours || '', start_date: st.start_date || '', due_date: st.due_date || '', assignees: (st.assignees || []).map(a => a.id || a) })}
                                    className="p-1.5 text-slate-400 active:bg-slate-100 rounded">
                                    <Edit2 className="h-3.5 w-3.5" />
                                  </button>
                                  <button onClick={async () => {
                                    if (!window.confirm('Delete this subtask?')) return;
                                    try { await subtaskAPI.deleteSubtask(st.id); setSubtasks(prev => prev.filter(s => s.id !== st.id)); } catch (err) { console.error(err); }
                                  }} className="p-1.5 text-red-400 active:bg-red-50 rounded">
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 mt-2 flex-wrap">
                                <select value={st.status} onChange={async (e) => {
                                  const newStatus = e.target.value;
                                  try { await subtaskAPI.updateSubtask(st.id, { status: newStatus }); setSubtasks(prev => prev.map(s => s.id === st.id ? { ...s, status: newStatus } : s)); } catch (err) { console.error(err); }
                                }} className="h-6 px-1.5 text-[10px] font-medium border border-slate-200 rounded bg-white outline-none" style={{ color: sCfg.color }}>
                                  {Object.entries(statusConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                </select>
                                {st.estimated_hours > 0 && <span className="text-[10px] text-slate-400">{st.estimated_hours}h est</span>}
                                {st.due_date && <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Calendar className="h-2.5 w-2.5" />{new Date(st.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>}
                                <div className="flex -space-x-1 ml-auto">
                                  {(st.assignees || []).slice(0, 4).map(a => (
                                    <div key={a.id} className="w-5 h-5 rounded-full bg-[#002FA7]/10 border border-white flex items-center justify-center" title={a.name}>
                                      <span className="text-[8px] font-bold text-[#002FA7]">{a.name?.[0]?.toUpperCase()}</span>
                                    </div>
                                  ))}
                                  {(st.assignees || []).length > 4 && <span className="text-[9px] text-slate-400 ml-1">+{(st.assignees || []).length - 4}</span>}
                                </div>
                              </div>
                            </div>
                            {subtaskHistory.id === st.id && (
                              <div className="ml-8 mb-2 p-2.5 rounded-lg bg-slate-50 border border-slate-100" data-testid={`subtask-history-${st.id}`}>
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">History</p>
                                {subtaskHistory.entries.length === 0 ? (
                                  <p className="text-xs text-slate-400">No history yet.</p>
                                ) : (
                                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                                    {subtaskHistory.entries.map(h => (
                                      <div key={h.id} className="flex items-start gap-2 text-[11px]">
                                        <span className="text-slate-400 flex-shrink-0 w-20">{new Date(h.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                                        <span className="text-slate-500"><span className="font-medium text-slate-700">{h.user?.name || 'System'}</span> {h.action}{h.changes && Object.keys(h.changes).length > 0 && `: ${Object.entries(h.changes).map(([k, v]) => `${k} → ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(', ')}`}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            </React.Fragment>
                          );
                        })}
                        {subtasks.length > 0 && (
                          <div className="flex items-center gap-3 pt-2 text-[10px] text-slate-400">
                            <span>{subtasks.filter(s => ['done','released'].includes(s.status)).length}/{subtasks.length} done</span>
                            <span>Est: {subtasks.reduce((sum, s) => sum + (s.estimated_hours || 0), 0)}h</span>
                            <span>Actual: {subtasks.reduce((sum, s) => sum + (s.actual_hours || 0), 0)}h</span>
                          </div>
                        )}
                      </div>
                    )}
                    {subtasks.length === 0 && !newSubtask && (
                      <div className="text-center py-8 text-slate-400 text-sm">No subtasks yet</div>
                    )}
                  </div>
                )}

                {/* ATTACHMENTS TAB */}
                {detailTab === 'attachments' && (
                  <div className="px-3 sm:px-6 py-4">
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <label className="px-3 py-1.5 text-xs font-medium bg-[#002FA7] text-white rounded-lg cursor-pointer hover:bg-[#0035c0] flex items-center gap-1.5">
                        <Upload size={13} /> Upload File<input type="file" className="hidden" onChange={handleFileUpload} disabled={uploadingFile} data-testid="file-upload-input" /></label>
                      <button onClick={() => setLinkDialogOpen(true)} className="px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-1.5" data-testid="add-link-btn"><Link2 size={13} /> Add Link</button>
                      {uploadingFile && <Loader2 size={14} className="animate-spin text-slate-400" />}
                    </div>
                    {linkDialogOpen && (
                      <div className="mb-4 p-3 border border-slate-200 rounded-lg bg-slate-50">
                        <input type="text" placeholder="Link name (optional)" value={newLinkName} onChange={(e) => setNewLinkName(e.target.value)} className="w-full h-8 px-2.5 mb-2 text-sm border border-slate-200 rounded outline-none" />
                        <div className="flex gap-2">
                          <input type="url" placeholder="https://drive.google.com/..." value={newLinkUrl} onChange={(e) => setNewLinkUrl(e.target.value)} className="flex-1 min-w-0 h-8 px-2.5 text-sm border border-slate-200 rounded outline-none" data-testid="link-url-input" />
                          <button onClick={handleAddLink} className="px-3 h-8 text-xs bg-[#002FA7] text-white rounded hover:bg-[#0035c0] flex-shrink-0">Add</button>
                          <button onClick={() => { setLinkDialogOpen(false); setNewLinkUrl(''); setNewLinkName(''); }} className="px-2 h-8 text-xs text-slate-500 border border-slate-200 rounded flex-shrink-0">Cancel</button>
                        </div>
                      </div>
                    )}
                    <div className="space-y-2">
                      {attachments.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No files attached.</p>}
                      {attachments.map(att => {
                        const isImage = att.file_type?.startsWith('image/'); const isLink = att.file_type === 'link';
                        const Icon = isImage ? Image : isLink ? Link2 : FileText;
                        return (
                          <div key={att.id} className="flex items-center gap-2 sm:gap-3 p-2.5 border border-slate-200 rounded-lg hover:border-slate-300 group" data-testid={`attachment-${att.id}`}>
                            <div className="p-1.5 rounded bg-slate-100 flex-shrink-0"><Icon className="h-4 w-4 text-slate-500" /></div>
                            <div className="flex-1 min-w-0">
                              <a href={att.url} target="_blank" rel="noreferrer" className="text-sm text-slate-700 hover:text-[#002FA7] truncate block font-medium">{att.filename}</a>
                              <p className="text-[10px] text-slate-400 truncate">{att.uploaded_by?.name} · {att.uploaded_at ? new Date(att.uploaded_at).toLocaleString() : ''}{att.size > 0 && ` · ${(att.size / 1024).toFixed(0)}KB`}</p>
                            </div>
                            <button onClick={() => handleDeleteAttachment(att.id)} className="p-1 text-slate-300 hover:text-red-500 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0" data-testid={`delete-attachment-${att.id}`}><Trash2 size={14} /></button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* COMMENTS TAB */}
                {detailTab === 'comments' && (
                  <div className="flex flex-col h-[400px]">
                    <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-4 space-y-3" data-testid="comments-list">
                      {comments.length === 0 && <div className="text-center text-slate-400 text-sm py-8">No comments yet.</div>}
                      {comments.map(c => (
                        <div key={c.id} className="flex gap-2 sm:gap-3" data-testid={`comment-${c.id}`}>
                          <div className="w-7 h-7 rounded-full bg-[#002FA7]/10 flex-shrink-0 flex items-center justify-center"><span className="text-[10px] font-bold text-[#002FA7]">{getInitials(c.user?.name)}</span></div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5 flex-wrap"><span className="text-xs font-semibold text-slate-800">{c.user?.name}</span><span className="text-[10px] text-slate-400">{formatFullDateTime(c.created_at)}</span></div>
                            <p className="text-sm text-slate-600 bg-slate-50 rounded-md px-3 py-2 break-words">{c.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="px-3 sm:px-6 py-3 border-t border-slate-200 flex items-center gap-2 flex-shrink-0">
                      <input type="text" value={newComment} onChange={(e) => setNewComment(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendComment()}
                        placeholder="Write a comment..." className="flex-1 min-w-0 h-9 px-3 rounded-md border border-slate-200 text-sm outline-none focus:border-[#002FA7]" data-testid="comment-input" />
                      <button onClick={handleSendComment} disabled={sendingComment || !newComment.trim()} className="h-9 w-9 rounded-md bg-[#002FA7] text-white flex items-center justify-center flex-shrink-0 disabled:opacity-50 hover:bg-[#002585]" data-testid="send-comment-btn">
                        {sendingComment ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}</button>
                    </div>
                  </div>
                )}

                {/* HISTORY TAB - Enhanced Audit Trail */}
                {detailTab === 'history' && (
                  <div className="px-3 sm:px-6 py-4 max-h-[450px] overflow-y-auto" data-testid="history-list">
                    {history.length === 0 && <div className="text-center text-slate-400 text-sm py-8">No activity recorded yet.</div>}
                    <div className="relative">
                      {/* Timeline line */}
                      {history.length > 0 && <div className="absolute left-3 top-3 bottom-3 w-px bg-slate-200" />}
                      <div className="space-y-0">
                        {history.map((h, idx) => {
                          const isCreate = h.action === 'created';
                          const isComment = h.action === 'commented';
                          const isStatusChange = h.action === 'status_changed';
                          const changes = h.changes || {};
                          const changeEntries = Object.entries(changes);

                          return (
                            <div key={h.id} className="relative pl-8 pb-4" data-testid={`history-${h.id}`}>
                              {/* Timeline dot */}
                              <div className={`absolute left-1.5 top-1 w-3 h-3 rounded-full border-2 border-white ${isCreate ? 'bg-green-500' : isStatusChange ? 'bg-blue-500' : isComment ? 'bg-purple-500' : 'bg-slate-400'}`} />

                              <div className="bg-slate-50 rounded-lg border border-slate-100 px-3 py-2.5">
                                {/* Header row: user + date */}
                                <div className="flex items-center justify-between mb-1.5">
                                  <div className="flex items-center gap-2">
                                    <div className="w-5 h-5 rounded-full bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0">
                                      <span className="text-[8px] font-bold text-[#002FA7]">{getInitials(h.user?.name)}</span>
                                    </div>
                                    <span className="text-xs font-semibold text-slate-700">{h.user?.name}</span>
                                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${isCreate ? 'bg-green-100 text-green-700' : isStatusChange ? 'bg-blue-100 text-blue-700' : isComment ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'}`}>
                                      {isCreate ? 'Created' : isStatusChange ? 'Status Change' : isComment ? 'Comment' : 'Updated'}
                                    </span>
                                  </div>
                                  <span className="text-[10px] text-slate-400 whitespace-nowrap">{formatFullDateTime(h.created_at)}</span>
                                </div>

                                {/* Change details */}
                                {isCreate && (
                                  <p className="text-xs text-slate-500">Created this task</p>
                                )}
                                {isComment && (
                                  <p className="text-xs text-slate-500">Added a comment</p>
                                )}
                                {changeEntries.length > 0 && !isCreate && (
                                  <div className="space-y-1 mt-1">
                                    {changeEntries.map(([field, change]) => (
                                      <div key={field} className="flex items-start gap-1.5 text-xs">
                                        <span className="font-medium text-slate-600 min-w-[90px] flex-shrink-0">{FIELD_LABELS[field] || field}:</span>
                                        <span className="text-slate-400 line-through">{formatHistoryValue(field, change.old)}</span>
                                        <span className="text-slate-400 mx-0.5">&rarr;</span>
                                        <span className="text-slate-700 font-medium">{formatHistoryValue(field, change.new)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-3 sm:px-6 py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-3 border-t border-slate-200 bg-slate-50 flex-shrink-0">
                <div>
                  {isManager && editingTask && (
                    <button onClick={async () => {
                      try {
                        if (editingTask.archived) {
                          await taskAPI.unarchiveTask(editingTask.id);
                          toast.success('Task unarchived');
                          setEditingTask(t => ({ ...t, archived: false }));
                        } else {
                          await taskAPI.archiveTask(editingTask.id);
                          toast.success('Task archived');
                          setEditingTask(t => ({ ...t, archived: true }));
                        }
                        if (viewMode === 'activities') { fetchActivities(); } else { fetchWorkItems(); }
                      } catch (err) { toast.error(err?.response?.data?.detail || 'Archive action failed'); }
                    }} className={`px-3 py-2 rounded-md text-sm font-medium flex items-center gap-1.5 border ${editingTask.archived ? 'text-emerald-700 border-emerald-200 bg-emerald-50 hover:bg-emerald-100' : 'text-amber-700 border-amber-200 bg-amber-50 hover:bg-amber-100'}`}
                      data-testid="archive-task-btn">
                      {editingTask.archived ? <><ArchiveRestore size={14} /> Unarchive</> : <><Archive size={14} /> Archive</>}
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2 sm:gap-3 justify-end">
                  <button className="px-4 py-2 rounded-md text-sm text-slate-600 border border-slate-200 bg-white hover:bg-slate-50" onClick={() => setDetailOpen(false)} data-testid="detail-cancel-btn">Cancel</button>
                  {(isManager || (editingTask && canEditTask(editingTask))) && (
                    <button className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-[#002FA7] hover:bg-[#002585] flex items-center gap-1.5 disabled:opacity-50" onClick={handleSaveEdit} disabled={saving} data-testid="detail-save-btn">
                      {saving && <Loader2 size={14} className="animate-spin" />} Save Changes</button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-[400px] rounded-md">
          <DialogHeader><DialogTitle className="font-heading font-bold">Upload Excel</DialogTitle></DialogHeader>
          <div className="mt-4">
            <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
              <Upload className="h-6 w-6 text-slate-400 mb-2" />
              <span className="text-sm text-slate-600">{uploading ? 'Uploading...' : 'Select .xlsx file'}</span>
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleExcelUpload} disabled={uploading} data-testid="excel-file-input" />
            </label>
          </div>
        </DialogContent>
      </Dialog>

      {/* Save View Dialog */}
      <Dialog open={saveViewOpen} onOpenChange={setSaveViewOpen}>
        <DialogContent className="sm:max-w-[360px] rounded-md">
          <DialogHeader><DialogTitle className="font-heading font-bold">Save Current View</DialogTitle></DialogHeader>
          <div className="mt-4 space-y-3">
            <input type="text" placeholder="View name (e.g. My Open Tasks)" value={newViewName} onChange={(e) => setNewViewName(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm outline-none focus:border-[#002FA7]" data-testid="view-name-input" autoFocus />
            <p className="text-xs text-slate-400">Saves: view mode, filters, sort, and column arrangement.</p>
            <button onClick={handleSaveView} disabled={!newViewName.trim()} className="w-full py-2 rounded-md text-sm font-semibold text-white bg-[#002FA7] hover:bg-[#002585] disabled:opacity-50" data-testid="confirm-save-view-btn">Save View</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sprint Completion Dialog */}
      <Dialog open={sprintCompleteOpen} onOpenChange={setSprintCompleteOpen}>
        <DialogContent className="sm:max-w-[560px] p-0 border border-slate-200 bg-white max-h-[100vh] sm:max-h-[80vh] overflow-hidden flex flex-col" data-testid="sprint-complete-dialog">
          <div className="px-6 pt-5 pb-3 border-b border-slate-100">
            <DialogHeader><DialogTitle className="font-heading text-lg font-bold text-slate-900">Complete Sprint</DialogTitle></DialogHeader>
            {sprintToComplete && <p className="text-sm text-slate-500 mt-1">Sprint: <strong className="text-slate-700">{sprintToComplete.name}</strong></p>}
          </div>
          <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-emerald-700">{sprintCompletedCount}</div>
                <div className="text-[10px] font-medium text-emerald-600 uppercase">Completed</div>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-amber-700">{sprintPendingTasks.length}</div>
                <div className="text-[10px] font-medium text-amber-600 uppercase">Pending</div>
              </div>
              <div className="bg-slate-50 border border-slate-100 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-slate-700">{sprintTotalCount}</div>
                <div className="text-[10px] font-medium text-slate-500 uppercase">Total</div>
              </div>
            </div>

            {sprintPendingTasks.length > 0 ? (
              <>
                <div>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                    {sprintPendingTasks.length} incomplete task{sprintPendingTasks.length > 1 ? 's' : ''} — where should they go?
                  </h4>
                  <div className="flex gap-2 mb-3">
                    <button onClick={() => setSprintMoveTarget('backlog')}
                      className={`flex-1 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${sprintMoveTarget === 'backlog' ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                      data-testid="move-to-backlog-btn">
                      Move to Backlog
                    </button>
                    {sprints.filter(s => s.status === 'active' && s.id !== sprintToComplete?.id).map(s => (
                      <button key={s.id} onClick={() => setSprintMoveTarget(s.id)}
                        className={`flex-1 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${sprintMoveTarget === s.id ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                        data-testid={`move-to-sprint-${s.id}`}>
                        Move to {s.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 flex items-center gap-2">
                    <AlertTriangle size={13} className="text-amber-500" />
                    <span className="text-xs font-semibold text-slate-600">Pending Tasks</span>
                  </div>
                  <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
                    {sprintPendingTasks.map(t => (
                      <div key={t.id} className="px-3 py-2 flex items-center gap-2.5 hover:bg-slate-50" data-testid={`pending-task-${t.id}`}>
                        <span className="text-[10px] font-mono text-slate-400">{t.task_number}</span>
                        <span className="text-sm text-slate-700 flex-1 truncate">{t.title}</span>
                        <span className="text-[10px] font-medium uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{t.status?.replace(/_/g, ' ')}</span>
                        <span className="text-[10px] text-slate-400">{t.estimated_hours || 0}h</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-6">
                <Check size={32} className="mx-auto text-emerald-500 mb-2" />
                <p className="text-sm font-medium text-slate-700">All tasks are completed!</p>
                <p className="text-xs text-slate-400 mt-1">This sprint can be closed cleanly.</p>
              </div>
            )}
          </div>
          <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-3">
            <button className="px-4 py-2 rounded-md text-sm text-slate-600 border border-slate-200 bg-white hover:bg-slate-50"
              onClick={() => setSprintCompleteOpen(false)} data-testid="sprint-complete-cancel-btn">Cancel</button>
            <button className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-orange-600 hover:bg-orange-500 flex items-center gap-1.5 disabled:opacity-50"
              onClick={handleConfirmSprintComplete} disabled={sprintCompleting} data-testid="sprint-complete-confirm-btn">
              {sprintCompleting && <Loader2 size={14} className="animate-spin" />} Complete Sprint
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkItems;